import { ChangeEvent, FormEvent, Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { ClipboardEvent as ReactClipboardEvent, DragEvent as ReactDragEvent, KeyboardEvent as ReactKeyboardEvent, ReactNode } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { ko } from "date-fns/locale/ko";
import { NumericFormat } from "react-number-format";
import "react-datepicker/dist/react-datepicker.css";
import { applyCategoryPreset, buildIntakeSteps, createDefaultIntakeData, createDraftId, FIXED_UPSELL_COMMISSION_RATE, getEffectiveIntakeSchema, INTAKE_STEPS, isDepositContractType, isUpsellContractType, LOCAL_STORAGE_KEY, MAX_LISTING_PHOTOS, normalizeContractTypeForCategory, parseOfferedContractTypes, REVIEW_BENEFIT_HELPER_TEXT, sanitizeUpsellRate } from "./form-schema";
import { getFirstStepIndexOfPart, getPartIndexForStepId, getPartSteps, INTAKE_PARTS } from "./form-schema/parts";
import { EXCLUSIVE_BENEFIT_DEFAULTS } from "./form-schema/guidance-defaults";
import { isDefaultLikeValue } from "./app/default-similarity";
import { getContractOptionsForContext, getDefaultUpsellRateForContext } from "./form-config";
import { getCategoryProfile } from "./category-profiles";
import type { IntakeCategoryProfile } from "./category-profiles";
import { DEFAULT_CATEGORY_TAXONOMY_CONFIG, getCategoryDisplayBreadcrumb, getCategoryDisplayLabel, getCategoryIdFromPathname, getCategoryPath, getVisibleCategoryGroups, normalizeCategoryTaxonomyConfig, type CategoryTaxonomyConfig, type TaxonomyCategoryGroup } from "./category-taxonomy";
import type { FormConfigVersion } from "./form-config";
import type { DraftState, FieldOption, FileUploadDefinition, IntakeCategoryId, IntakeField, IntakeStep, ProductOption, RepeaterField, UploadedFile } from "./types";
import type { IntakeImportResult } from "../../shared/intake-import";
import {
  createDoctorPhotoUploadDefinition,
  createProductDetailPhotoUploadDefinition,
  formatBytes,
  formatDateTime,
  HERO_IMAGE_DETAIL_TARGET_RATIO,
  HERO_IMAGE_LIST_CARD_VISIBLE_RATIO,
  isUploadedFileForDefinition,
  reindexDoctorPhotoFilesAfterRemoval,
  reindexProductDetailPhotoFilesAfterRemoval,
  sanitizeFileBaseName,
  splitFileName,
  uploadLimitDescription,
} from "./app/upload-utils";
import { ADMIN_FORM_CONFIG_DRAFT_STORAGE_KEY, fetchPublishedCategoryTaxonomy, fetchPublishedFormConfig, loadAdminDraftFormConfig, parseStoredFormConfig, shouldUseLocalDraftFormConfigPreview, type CategoryTaxonomyLoadStatus } from "./app/public-config";
import { fieldValueToString, getPathValue, setPathValue, withObjectParticle, withSubjectParticle } from "./app/value-utils";
import { createInitialDraft, getProductPricingMode, loadStoredDraft, normalizeIntakeData, normalizeProductOptionPricing, nowIso, type ProductPricingMode } from "./app/draft-state";
import { applyIntakeImportPatches, describeIntakeImportPatch, isCurrentIntakeImportRequest, isSameIntakeImportContext, shouldClearAdminIntakeImportNoticeOnSchemaChange, shouldShowAdminIntakeImport, type IntakeImportContext } from "./app/intake-import";
import { createSubmissionPresentationManifest } from "./app/submission-document";
import { autosavePartnerDraft, createDraftFromInvite, createPartnerDraft, deletePartnerFile, fetchPartnerDraftSync, fetchPartnerInvite, getAdminPasswordHelperText, getInviteIdFromPathname, hasMeaningfulInviteLocalDraft, hasMeaningfulPartnerEdit, isPartnerDraftCapabilityConflictError, isPartnerDraftVersionConflictError, loadStoredPartnerServerBase, partnerDraftFingerprint, partnerServerBaseFromDraft, storePartnerDraftLocally, storePartnerServerBaseLocally, submitPartnerDraft, uploadPartnerFile, type PartnerInvite, type PartnerServerBase } from "./app/partner-api";
import { mergeIntakeData, mergeUploadedFiles } from "./app/partner-draft-merge";
import { createAsyncMutationQueue } from "./app/partner-mutation-queue";
import { getPartnerSaveStatusMessage, type PartnerSaveStatus, type PartnerSaveTarget } from "./app/partner-collaboration-status";
import { CategoryLanding, CategoryTaxonomyLoadScreen } from "./app/category-components";
import { describeUploadFailure, validateUploadSelection, type UploadFailure } from "./app/upload-failure";
import {
  applyProductCellValue,
  downloadProductSampleExcel,
  getPricingModeDescription,
  getProductCellValue,
  getProductColumnState,
  getProductPasteColumns,
  isProductHeaderRow,
  PRODUCT_DETAIL_FIELDS,
  PRODUCT_TABLE_COLUMNS,
  type ProductColumnKey,
  type ProductDetailKey,
  type ProductTableColumn,
} from "./app/product-spreadsheet-model";

type UploadState = "uploading" | "done" | UploadFailure | undefined;
type UploadStatus = Record<string, UploadState>;
type AiFillAction = "storeNames" | "contentBasics" | "benefits" | "policy" | "procedureDetails";
type FieldGroupDefinition = {
  title: string;
  description?: string;
  detail?: string;
  fieldIds: string[];
  defaultOpen?: boolean;
  aiAction?: AiFillAction;
};

type AiFillResponse = {
  summary?: string;
  patches: Array<{ path: string; value: string; reason?: string }>;
};

type AdminIntakeImportProposal = IntakeImportContext & {
  result: IntakeImportResult;
};

function createIntakeImportContext(draft: Pick<DraftState, "draftId" | "data">, schemaHash: string): IntakeImportContext {
  return { draftId: draft.draftId, categoryId: draft.data.category.categoryId, schemaHash };
}

type StepNotice = { message: string; stepId: string };
type CoeditNotice = { kind: "updated" | "conflict"; message: string };

const PARTNER_DRAFT_CONFLICT_MESSAGE = "다른 곳의 저장과 계속 겹치고 있어요. 잠시 후 자동으로 다시 저장합니다.";
const PARTNER_AUTOSAVE_DEBOUNCE_MS = 2500;
// 서버 초안 생성 실패는 짧게 재시도하고, 이후는 다음 입력/재접속을 기다려 생성 레이트 리밋을 아낀다.
const PARTNER_DRAFT_CREATE_RETRY_DELAYS_MS = [3000, 10000, 30000];
const PARTNER_DRAFT_SYNC_INTERVAL_MS = 10_000;
const PARTNER_SAVE_MAX_ATTEMPTS = 3;
const TAX_INVOICE_CONTACT_FIELD_IDS = ["taxInvoiceContactName", "taxInvoiceContactPhone", "taxInvoiceContactEmail"];

const COEDIT_SECTION_LABELS: Record<string, string> = {
  category: "카테고리",
  company: "기본 정보",
  reservation: "예약 정보",
  contract: "계약 정보",
  products: "상품 구성",
  content: "콘텐츠 정보",
  language: "언어 응대",
  policy: "정책·주의사항",
  documents: "동의 항목",
  doctors: "의료진·전문가 소개",
  procedures: "시술 정보",
};

type PartnerResolvedSave =
  | { status: "success"; draftVersion: number; updatedAt: string; hasSavedAdminPassword: boolean; savedData: DraftState["data"]; savedFiles: DraftState["files"] }
  | { status: "submitted" }
  | { status: "expired" }
  | { status: "error"; message: string };

type InviteBootstrapState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "notFound" }
  | { status: "expired" }
  | { status: "submitted" }
  | { status: "error"; message: string }
  | { status: "conflict"; draft: DraftState };

type AdminUser = { email: string; name?: string };

type InviteCreateState =
  | { status: "idle" }
  | { status: "creating" }
  | { status: "created"; url: string; copied: boolean }
  | { status: "error"; message: string };

type ExclusiveBenefitKey = "package" | "discount" | "gift";

type ExclusiveBenefitOption = {
  key: ExclusiveBenefitKey;
  label: string;
  description: string;
  placeholder: string;
  defaultValue: string;
};

type ExclusiveBenefitEntry = {
  selected: boolean;
  text: string;
};

const EXCLUSIVE_BENEFIT_OPTIONS: ExclusiveBenefitOption[] = [
  {
    key: "package",
    label: "단독 패키지 구성",
    description: "크리에이트립에서만 예약할 수 있는 묶음 상품",
    placeholder: "구성 상품과 이용 조건을 적어주세요",
    defaultValue: EXCLUSIVE_BENEFIT_DEFAULTS.package,
  },
  {
    key: "discount",
    label: "단독 할인",
    description: "크리에이트립 예약자에게만 제공하는 할인가",
    placeholder: "할인 대상 상품과 적용 조건을 적어주세요",
    defaultValue: EXCLUSIVE_BENEFIT_DEFAULTS.discount,
  },
  {
    key: "gift",
    label: "단독 혜택",
    description: "방문 또는 결제 시 제공하는 증정/서비스 혜택",
    placeholder: "증정 또는 추가 서비스의 조건을 적어주세요",
    defaultValue: EXCLUSIVE_BENEFIT_DEFAULTS.gift,
  },
];

const BASE_LANGUAGE_OPTIONS = ["영어", "일본어", "중국어"];
const OTHER_LANGUAGE_OPTIONS = [
  "태국어",
  "베트남어",
  "스페인어",
  "프랑스어",
  "독일어",
  "러시아어",
  "아랍어",
  "인도네시아어",
  "말레이어",
  "힌디어",
  "타밀어",
  "벵골어",
  "우르두어",
  "네팔어",
  "싱할라어",
  "몽골어",
  "튀르키예어",
  "이탈리아어",
  "포르투갈어",
  "네덜란드어",
  "스웨덴어",
  "폴란드어",
  "우크라이나어",
  "카자흐어",
  "필리핀어",
  "크메르어",
  "미얀마어",
];
const LANGUAGE_SUPPORT_STATUSES = ["상주", "예약 시 배정 가능", "간단 응대만 가능"];
const DEFAULT_LANGUAGE_SUPPORT_STATUS = "간단 응대만 가능";
const FIRST_SURVEY_STEP_INDEX = 1;

type LanguageSupportEntry = {
  language: string;
  status: string;
  custom: boolean;
};

type KakaoPlace = {
  id: string;
  placeName: string;
  categoryName: string;
  categoryGroupCode: string;
  categoryGroupName: string;
  phone: string;
  addressName: string;
  roadAddressName: string;
  placeUrl: string;
  x: string;
  y: string;
  distance?: string;
};

type KakaoPlaceLookupState = {
  open: boolean;
  query: string;
  loading: boolean;
  items: KakaoPlace[];
  error: string | null;
  searchedQuery: string | null;
};

const initialKakaoLookupState: KakaoPlaceLookupState = {
  open: false,
  query: "",
  loading: false,
  items: [],
  error: null,
  searchedQuery: null,
};

const STEP_FIELD_GROUPS: Record<string, FieldGroupDefinition[]> = {
  company: [
    { title: "상점명", description: "한국어 이름만 필수예요. 다른 언어 표기는 있으면 함께 적어주세요.", fieldIds: ["storeNameKo", "storeNameEn", "storeNameJa", "storeNameZh"], defaultOpen: true, aiAction: "storeNames" },
    { title: "위치와 참고 링크", fieldIds: ["address", "representativePhone", "kakaoMapsUrl", "kakaoCategory", "googleMapsUrl", "websiteUrl", "instagramUrl", "contentReferenceUrl"] },
    { title: "담당자, 예약 알림과 정산", description: "예약 접수 알림과 세금계산서 수신에 사용할 담당자 정보를 확인해주세요.", fieldIds: ["contactName", "contactRole", "contactDepartment", "contactEmail", "contactPhone", "useContactEmailForNotifications", "notificationEmails", "useContactPhoneForAlimtalk", "notificationPhones", "useContactInfoForTaxInvoice", "taxInvoiceContactName", "taxInvoiceContactPhone", "taxInvoiceContactEmail"] },
  ],
  reservation: [
    { title: "예약 오픈 범위", fieldIds: ["bookableStartOffsetDays", "bookableWindowMonths"], defaultOpen: true },
    { title: "운영 시간", description: "영업시간과 예약 가능 시간을 입력해주세요.", fieldIds: ["businessHours", "reservationHours", "reservationUnit"], defaultOpen: true },
    { title: "예약 확정", description: "대부분은 메일로 확인 후 확정하면 충분합니다.", fieldIds: ["confirmationMode", "communicationChannel", "adminAccountNeeded", "adminLoginId", "adminPassword"], defaultOpen: true },
  ],
  contract: [
    { title: "계약 구조", fieldIds: ["contractType", "upsellCommissionRate"], defaultOpen: true },
    {
      title: "보증금/정산 메모",
      description: "기본 정책과 다른 요청이 있을 때만 작성해주세요.",
      detail: "• 보증금은 상품별로 설정 가능하며 보통 1~2만원 선 권장\n• 판매가/정산가 방식은 판매가와 정산가 기준을 미리 정리\n• 방문 후 현장결제 시 보증금 차감 후 차액 결제\n• 미진행/노쇼/환불 시 정산 제외 조건을 함께 확인",
      fieldIds: ["depositPolicyMemo", "settlementMemo"],
    },
  ],
  content: [
    { title: "한 줄 소구", fieldIds: ["catchphrase", "sellingPointTags", "touristConvenienceOptions", "primarySellingPoint", "targetCustomers", "targetCustomerConcernDetail"], defaultOpen: true, aiAction: "contentBasics" },
    { title: "외국어 응대와 서면 주의사항", description: "외국어 응대 가능 여부와 고객에게 주의사항을 문서·안내문으로 제공할 수 있는 언어를 함께 확인해주세요.", fieldIds: ["availableLanguages", "cautionLanguages"], defaultOpen: true },
    { title: "혜택과 추천 이유", fieldIds: ["recommendationReason", "creatripExclusiveBenefitStatus", "creatripExclusiveBenefit", "reviewBenefit"] },
    { title: "업체/상품 강점", fieldIds: ["clinicStrengths", "serviceEnvironment", "referenceLinks"] },
    {
      title: "구글맵 연동",
      detail: "크리에이트립은 구글과의 파트너십을 통해 제휴 업체 구글맵에 게시된 구글 리뷰 일부를 노출하고, 구글맵 온라인 예약 버튼 연동을 검토할 수 있습니다. 추가 비용 없이 제공되며 예약 유입 확대와 고객 경험 향상에 도움이 됩니다.",
      fieldIds: ["googleReviewIntegration", "googleBookingIntegration"],
      defaultOpen: true,
    },
  ],
  policy: [
    { title: "예약/환불", fieldIds: ["cancellationRefundPolicy", "bookingQuestionChecklist", "bookingQuestions"], defaultOpen: true, aiAction: "policy" },
    { title: "방문 흐름", fieldIds: ["visitFlow"], defaultOpen: true },
    { title: "간단 선택 항목", description: "예약페이지에 표시할 기본 제한사항입니다.", fieldIds: ["minorPolicy", "passportNeeded", "healthRestrictions", "consultationFeePolicy", "consultationFeeAmount"], defaultOpen: true },
    { title: "주의사항", fieldIds: ["otherPrecautions"], defaultOpen: true },
  ],
  photos: [
    { title: "이미지 사용 확인", fieldIds: ["imageUsageConsent"], defaultOpen: true },
  ],
  documents: [
    { title: "서류 제출 확인", fieldIds: ["documentAccuracyConsent"], defaultOpen: true },
  ],
};

function getStepFieldGroups(stepId: string, contractType: string, categoryProfile: IntakeCategoryProfile | null): FieldGroupDefinition[] | undefined {
  const groups = STEP_FIELD_GROUPS[stepId];
  if (!groups) return undefined;
  return groups.map((group) => {
    if (stepId === "contract" && group.fieldIds.includes("settlementMemo")) {
      if (contractType === "settlement") {
        return {
          ...group,
          title: "판매가와 정산가",
          description: "판매가와 정산가 기준이 다른 경우 작성해주세요.",
          detail: "• 고객 판매가와 파트너 정산가를 미리 정해 운영할 때 사용\n• 정산가는 VAT 포함/별도 기준을 함께 확인\n• 품절·사용 불가·환불 시 정산 제외 조건을 함께 정리",
        };
      }
      if (isDepositContractType(contractType)) {
        return {
          ...group,
          title: "보증금 메모",
          description: "기본 보증금 정책과 다른 요청이 있을 때만 작성해주세요.",
          detail: "• 보증금은 상품별로 설정 가능하며 보통 1~2만원 선 권장\n• 방문 후 현장결제 시 보증금 차감 후 차액 결제\n• 미진행/노쇼/환불 시 정산 제외 조건을 함께 확인",
        };
      }
      return group;
    }
    if (stepId === "content" && group.fieldIds.includes("clinicStrengths")) {
      return {
        ...group,
        title: categoryProfile ? `${categoryProfile.domainLabel} 강점과 참고자료` : "업체/상품 강점",
      };
    }
    return group;
  });
}

const REPEATER_FIELD_GROUPS: Record<string, FieldGroupDefinition[]> = {
  products: [
    { title: "상품명과 시간", fieldIds: ["optionName", "duration", "maxQuantityPerSlot"], defaultOpen: true },
    { title: "가격", fieldIds: ["listPrice", "salePrice", "depositOrSettlementPrice"] },
    { title: "구성/추천 이유", fieldIds: ["includedItems", "excludedItems", "recommendationReason"] },
  ],
  doctors: [
    { title: "기본 정보", fieldIds: ["name", "role"], defaultOpen: true },
    { title: "이력", fieldIds: ["profileText", "referenceUrl"], defaultOpen: true },
  ],
  procedures: [
    { title: "시술 개요", fieldIds: ["treatmentName", "description", "duration", "effectDuration"], defaultOpen: true, aiAction: "procedureDetails" },
  ],
};

registerLocale("ko", ko);

function isProductMeaningfullyFilled(product: unknown): product is ProductOption {
  if (!product || typeof product !== "object") return false;
  const row = product as Partial<ProductOption>;
  const meaningfulKeys: Array<keyof ProductOption> = [
    "optionName",
    "listPrice",
    "salePrice",
    "depositAmount",
    "settlementPrice",
    "maxQuantityPerSlot",
    "duration",
    "includedItems",
    "excludedItems",
    "recommendationReason",
  ];
  return meaningfulKeys.some((key) => fieldValueToString(row[key]).trim().length > 0);
}

function getEffectiveProducts(products: ProductOption[]): ProductOption[] {
  return products.filter(isProductMeaningfullyFilled);
}

function contractTypeLabel(value: string, rate: string): string {
  if (value === "free_reservation_upsell") return `무료예약 + 업셀링 ${rate}%`;
  if (value === "deposit_upsell") return `보증금 결제 + 업셀링 ${rate}%`;
  if (value === "deposit") return "보증금 결제 방식 (업셀링 아님)";
  if (value === "settlement") return "판매가 판매 후 정산가 정산 방식";
  return "미선택";
}

function contractTypeSummary(value: string, rate: string): string {
  if (isUpsellContractType(value)) return `업셀링 수수료 ${rate}% (계약 과정에서 확정)`;
  if (value === "deposit") return "보증금 결제 방식 · 업셀링 없음";
  if (value === "settlement") return "판매가 판매 후 정산가 기준 정산";
  return "계약 구조를 선택해주세요.";
}

function stepIndexById(stepId: string, steps: IntakeStep[] = INTAKE_STEPS): number {
  const index = steps.findIndex((step) => step.id === stepId);
  return index >= 0 ? index : 0;
}

function parseMultiCheckboxValue(value: unknown): string[] {
  return fieldValueToString(value)
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function toggleMultiCheckboxValue(value: unknown, optionValue: string, checked: boolean, options: IntakeField["options"]): string {
  const selected = new Set(parseMultiCheckboxValue(value));
  if (checked) selected.add(optionValue);
  else selected.delete(optionValue);
  return (options ?? []).map((option) => option.value).filter((value) => selected.has(value)).join("\n");
}

function createEmptyExclusiveBenefitEntries(): Record<ExclusiveBenefitKey, ExclusiveBenefitEntry> {
  return EXCLUSIVE_BENEFIT_OPTIONS.reduce((entries, option) => {
    entries[option.key] = { selected: false, text: "" };
    return entries;
  }, {} as Record<ExclusiveBenefitKey, ExclusiveBenefitEntry>);
}

function parseExclusiveBenefitValue(value: unknown): Record<ExclusiveBenefitKey, ExclusiveBenefitEntry> {
  const entries = createEmptyExclusiveBenefitEntries();
  const rawText = fieldValueToString(value).trim();
  if (!rawText) return entries;

  const optionByHeading = new Map(EXCLUSIVE_BENEFIT_OPTIONS.map((option) => [`[${option.label}]`, option]));
  let currentKey: ExclusiveBenefitKey | null = null;
  let foundStructuredSection = false;

  rawText.split("\n").forEach((line) => {
    const heading = optionByHeading.get(line.trim());
    if (heading) {
      currentKey = heading.key;
      entries[currentKey].selected = true;
      foundStructuredSection = true;
      return;
    }
    if (currentKey) entries[currentKey].text = [entries[currentKey].text, line].filter(Boolean).join("\n");
  });

  if (!foundStructuredSection) {
    entries.gift = { selected: true, text: rawText };
    return entries;
  }

  EXCLUSIVE_BENEFIT_OPTIONS.forEach((option) => {
    entries[option.key].text = entries[option.key].text.trim();
  });
  return entries;
}

function serializeExclusiveBenefitEntries(entries: Record<ExclusiveBenefitKey, ExclusiveBenefitEntry>): string {
  return EXCLUSIVE_BENEFIT_OPTIONS
    .filter((option) => entries[option.key].selected)
    .map((option) => [`[${option.label}]`, entries[option.key].text.trim()].filter(Boolean).join("\n"))
    .join("\n\n");
}

function normalizeLanguageSupportStatus(value: string): string {
  return LANGUAGE_SUPPORT_STATUSES.includes(value) ? value : DEFAULT_LANGUAGE_SUPPORT_STATUS;
}

function normalizeLanguageName(value: string): string {
  const language = value.trim();
  if (["중국어 간체", "중국어 번체", "중국어(간체)", "중국어(번체)"].includes(language)) return "중국어";
  return language;
}

function parseLanguageSupportValue(value: unknown): LanguageSupportEntry[] {
  const entries = fieldValueToString(value)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [languagePart, statusPart] = line.split(/\s[-–—|:]\s/);
      const language = normalizeLanguageName(languagePart ?? "");
      const status = normalizeLanguageSupportStatus((statusPart ?? "").trim());
      return language ? { language, status, custom: !BASE_LANGUAGE_OPTIONS.includes(language) } : null;
    })
    .filter((entry): entry is LanguageSupportEntry => entry !== null);

  return Array.from(entries.reduce((deduped, entry) => {
    if (!deduped.has(entry.language)) deduped.set(entry.language, entry);
    return deduped;
  }, new Map<string, LanguageSupportEntry>()).values());
}

function formatLanguageSupportValue(entries: LanguageSupportEntry[]): string {
  return entries
    .filter((entry) => entry.language.trim())
    .map((entry) => `${entry.language.trim()} - ${normalizeLanguageSupportStatus(entry.status)}`)
    .join("\n");
}

type KakaoPlacePatch = { path: string; label: string; value: string };

function createKakaoPlacePatches(place: KakaoPlace): KakaoPlacePatch[] {
  const address = place.roadAddressName || place.addressName;
  return [
    { path: "company.storeNameKo", label: "상점명 / 병원명(한국어)", value: place.placeName },
    { path: "company.address", label: "주소", value: address },
    { path: "company.representativePhone", label: "업체 대표 전화번호", value: place.phone },
    { path: "company.kakaoMapsUrl", label: "카카오맵 링크", value: place.placeUrl },
    { path: "company.kakaoCategory", label: "카카오맵 분류", value: place.categoryName },
    { path: "company.kakaoPlaceName", label: "카카오맵 원본 장소명", value: place.placeName },
    { path: "company.kakaoPlaceId", label: "카카오맵 장소 ID", value: place.id },
    { path: "company.kakaoRoadAddress", label: "카카오맵 도로명 주소", value: place.roadAddressName },
    { path: "company.kakaoJibunAddress", label: "카카오맵 지번 주소", value: place.addressName },
    { path: "company.kakaoCategoryGroupCode", label: "카카오맵 카테고리 그룹 코드", value: place.categoryGroupCode },
    { path: "company.kakaoCategoryGroupName", label: "카카오맵 카테고리 그룹", value: place.categoryGroupName },
    { path: "company.kakaoLongitude", label: "카카오맵 경도(x)", value: place.x },
    { path: "company.kakaoLatitude", label: "카카오맵 위도(y)", value: place.y },
    { path: "company.kakaoDistanceMeters", label: "검색 기준 거리(m)", value: place.distance ?? "" },
  ].filter((patch) => patch.value.trim());
}

function formatKakaoPlaceAddress(place: KakaoPlace): string {
  return place.roadAddressName || place.addressName || "주소 정보 없음";
}

function formatDateValue(date: Date | null): string {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseDateValue(value: unknown): Date | null {
  const text = fieldValueToString(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null;
  const [year, month, day] = text.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function parseDateRangeValue(value: unknown): [Date | null, Date | null] {
  const [start, end] = fieldValueToString(value).split("..");
  return [parseDateValue(start), parseDateValue(end)];
}

function formatDateRangeValue(start: Date | null, end: Date | null): string {
  const startText = formatDateValue(start);
  const endText = formatDateValue(end);
  if (!startText && !endText) return "";
  return `${startText}..${endText}`;
}

function shouldSkipRequiredField(field: IntakeField, draft: DraftState): boolean {
  if (field.id === "adminPassword" && draft.hasSavedAdminPassword) return true;
  if (["adminLoginId", "adminPassword"].includes(field.id)) {
    return draft.data.reservation.adminAccountNeeded !== true;
  }
  if (TAX_INVOICE_CONTACT_FIELD_IDS.includes(field.id)) {
    return draft.data.company.useContactInfoForTaxInvoice === true;
  }
  return false;
}

type MissingRequiredItemKind = "category" | "field" | "repeater" | "upload";

type MissingRequiredItem = {
  key: string;
  label: string;
  stepId: string;
  stepTitle: string;
  stepIndex: number;
  kind: MissingRequiredItemKind;
  focusId?: string;
};

function uniqueMissingRequiredItems(items: MissingRequiredItem[]): MissingRequiredItem[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.key)) return false;
    seen.add(item.key);
    return true;
  });
}

function createMissingRequiredItem(step: IntakeStep, stepIndex: number, kind: MissingRequiredItemKind, label: string, focusId?: string): MissingRequiredItem {
  return {
    key: `${kind}:${step.id}:${focusId ?? label}`,
    label,
    stepId: step.id,
    stepTitle: step.title,
    stepIndex,
    kind,
    focusId,
  };
}

function productFieldFocusId(rowIndex: number, fieldPath: string): string {
  return `product-cell-${rowIndex}-${fieldPath}`;
}

function isRequiredFieldValueMissing(field: IntakeField, value: unknown): boolean {
  if (!field.required) return false;
  return field.type === "checkbox" ? value !== true : !fieldValueToString(value).trim();
}

type RequiredValidationNavigation =
  | { kind: "next"; stepId: string; missingCount: number }
  | { kind: "return" | "missingJump"; stepId: string };

export function updateRevealedMissingStepIds(current: Set<string>, navigation: RequiredValidationNavigation): Set<string> {
  if (navigation.kind === "next" && navigation.missingCount === 0) return current;
  if (current.has(navigation.stepId)) return current;
  const next = new Set(current);
  next.add(navigation.stepId);
  return next;
}

export function shouldRevealMissingRequiredForField(field: IntakeField, draft: DraftState, stepValidationRevealed: boolean): boolean {
  return stepValidationRevealed && !shouldSkipRequiredField(field, draft);
}

function collectMissingRequiredItemsForStep(step: IntakeStep, draft: DraftState, stepIndex: number): MissingRequiredItem[] {
  const missing: MissingRequiredItem[] = [];
  step.fields?.forEach((field) => {
    if (shouldSkipRequiredField(field, draft)) return;
    const value = getPathValue(draft.data, field.path);
    if (isRequiredFieldValueMissing(field, value)) missing.push(createMissingRequiredItem(step, stepIndex, "field", field.label, field.id));
  });

  step.repeaters?.forEach((repeater) => {
    const items = getPathValue(draft.data, repeater.path);
    if (!Array.isArray(items)) return;
    const rows = repeater.id === "products"
      ? items.map((item, index) => ({ item, index })).filter(({ item }) => isProductMeaningfullyFilled(item))
      : items.map((item, index) => ({ item, index }));
    if (repeater.id === "products" && rows.length === 0) {
      missing.push(createMissingRequiredItem(step, stepIndex, "repeater", "상품 옵션 1: 옵션명", productFieldFocusId(0, "optionName")));
      return;
    }
    rows.forEach(({ item, index }, displayIndex) => {
      repeater.fields.forEach((field) => {
        if (!field.required) return;
        const value = (item as Record<string, unknown>)[field.path];
        if (fieldValueToString(value).trim()) return;
        const focusId = repeater.id === "products" ? productFieldFocusId(index, field.path) : `${repeater.id}-${index}-${field.id}`;
        missing.push(createMissingRequiredItem(step, stepIndex, "repeater", `${repeater.label} ${displayIndex + 1}: ${field.label}`, focusId));
      });
    });
  });

  step.fileUploads?.forEach((upload) => {
    if (!upload.required) return;
    const hasFile = draft.files.some((file) => file.category === upload.category);
    if (!hasFile) missing.push(createMissingRequiredItem(step, stepIndex, "upload", upload.label, `upload-${upload.id}`));
  });
  return uniqueMissingRequiredItems(missing);
}

function collectMissingRequiredForStep(step: IntakeStep, draft: DraftState): string[] {
  return collectMissingRequiredItemsForStep(step, draft, -1).map((item) => item.label);
}

function collectMissingRequiredItemsFromSteps(draft: DraftState, steps: IntakeStep[]): MissingRequiredItem[] {
  const categoryMissing: MissingRequiredItem[] = draft.data.category.categoryId
    ? []
    : [{ key: "category:category", label: "상품 유형", stepId: "welcome", stepTitle: "상품 유형 선택", stepIndex: 0, kind: "category" }];
  return uniqueMissingRequiredItems([...categoryMissing, ...steps.flatMap((candidate, index) => collectMissingRequiredItemsForStep(candidate, draft, index))]);
}

function collectMissingRequiredItems(draft: DraftState, config?: FormConfigVersion): MissingRequiredItem[] {
  return collectMissingRequiredItemsFromSteps(draft, buildIntakeSteps(draft.data, config));
}

function missingRequiredLabels(items: MissingRequiredItem[]): string[] {
  return items.map((item) => item.label);
}

function findMissingTargetElement(item: MissingRequiredItem): HTMLElement | null {
  if (!item.focusId || typeof document === "undefined") return null;
  return document.getElementById(item.focusId) ?? document.querySelector<HTMLElement>(`[data-missing-target="${item.focusId}"]`);
}

function openParentDetails(element: HTMLElement): void {
  let current: HTMLElement | null = element;
  while (current) {
    if (current instanceof HTMLDetailsElement) current.open = true;
    current = current.parentElement;
  }
}

function scrollToMissingTarget(item: MissingRequiredItem): void {
  const target = findMissingTargetElement(item);
  if (!target) {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  openParentDetails(target);
  const highlightTarget = target.closest<HTMLElement>(".field, .upload-card, .product-sheet-table td, .repeater-section, .product-sheet-section") ?? target;
  highlightTarget.classList.add("missing-target-highlight");
  window.setTimeout(() => highlightTarget.classList.remove("missing-target-highlight"), 2200);
  target.scrollIntoView({ behavior: "smooth", block: "center" });
  const focusTarget = target.matches("input, textarea, select, button")
    ? target
    : target.querySelector<HTMLElement>("input:not(:disabled), textarea:not(:disabled), select:not(:disabled), button:not(:disabled)");
  focusTarget?.focus({ preventScroll: true });
}

function getStepCompletion(step: IntakeStep, draft: DraftState): { done: number; total: number } {
  let total = 0;
  let done = 0;
  step.fields?.forEach((field) => {
    if (!field.required || shouldSkipRequiredField(field, draft)) return;
    total += 1;
    const value = getPathValue(draft.data, field.path);
    const isDone = field.type === "checkbox" ? value === true : Boolean(fieldValueToString(value).trim());
    if (isDone) done += 1;
  });
  step.repeaters?.forEach((repeater) => {
    const items = getPathValue(draft.data, repeater.path);
    if (!Array.isArray(items)) return;
    const rows = repeater.id === "products" ? items.filter(isProductMeaningfullyFilled) : items;
    repeater.fields.forEach((field) => {
      if (!field.required) return;
      total += 1;
      const hasAny = rows.some((item) => fieldValueToString((item as Record<string, unknown>)[field.path]).trim());
      if (hasAny) done += 1;
    });
  });
  step.fileUploads?.forEach((upload) => {
    if (!upload.required) return;
    total += 1;
    if (draft.files.some((file) => file.category === upload.category)) done += 1;
  });
  return { done, total };
}

export default function App() {
  const storedDraftRef = useRef<DraftState | null>(null);
  const pendingMissingTargetRef = useRef<MissingRequiredItem | null>(null);
  const partnerDraftCreatePromiseRef = useRef<Promise<{ inviteId: string; draftVersion: number } | null> | null>(null);
  const partnerDraftCreateRetryRef = useRef<{ attempt: number; timeout: number | null }>({ attempt: 0, timeout: null });
  const partnerDraftIdRecoveredRef = useRef(false);
  const partnerDraftCreateErrorRef = useRef<string | null>(null);
  const partnerAutosaveRef = useRef<{ promise: Promise<PartnerResolvedSave> } | null>(null);
  const partnerAutosaveTimeoutRef = useRef<number | null>(null);
  const partnerMutationQueueRef = useRef(createAsyncMutationQueue());
  const activeServerInviteIdRef = useRef<string | null>(null);
  const partnerServerBaseRef = useRef<PartnerServerBase | null>(null);
  const partnerSyncInFlightRef = useRef(false);
  const [pathname, setPathname] = useState(() => (typeof window === "undefined" ? "/" : window.location.pathname));
  const [draft, setDraft] = useState<DraftState>(() => {
    if (typeof window === "undefined") return createInitialDraft();
    const stored = loadStoredDraft();
    storedDraftRef.current = stored;
    return getInviteIdFromPathname(window.location.pathname) ? createInitialDraft() : stored ?? createInitialDraft();
  });
  const latestDraftRef = useRef(draft);
  const adminIntakeImportRequestRef = useRef(0);
  latestDraftRef.current = draft;
  const [showResumePrompt, setShowResumePrompt] = useState(() => Boolean(storedDraftRef.current && !storedDraftRef.current.submittedAt && !getInviteIdFromPathname(typeof window === "undefined" ? "/" : window.location.pathname)));
  const [inviteBootstrap, setInviteBootstrap] = useState<InviteBootstrapState>({ status: "idle" });
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [inviteCreateState, setInviteCreateState] = useState<InviteCreateState>({ status: "idle" });
  const [saveStatus, setSaveStatus] = useState<PartnerSaveStatus>("saved");
  const [partnerDraftCreateRetryTick, setPartnerDraftCreateRetryTick] = useState(0);
  const [partnerSaveTarget, setPartnerSaveTarget] = useState<PartnerSaveTarget>("local");
  const [coeditNotice, setCoeditNotice] = useState<CoeditNotice | null>(null);
  const [isStepTransitionSaving, setIsStepTransitionSaving] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({});
  const [aiLoadingKey, setAiLoadingKey] = useState<string | null>(null);
  const [aiNotice, setAiNotice] = useState<StepNotice | null>(null);
  const [adminIntakeImportSourceText, setAdminIntakeImportSourceText] = useState("");
  const [adminIntakeImportProposal, setAdminIntakeImportProposal] = useState<AdminIntakeImportProposal | null>(null);
  const [adminIntakeImportLoading, setAdminIntakeImportLoading] = useState(false);
  const [adminIntakeImportError, setAdminIntakeImportError] = useState<string | null>(null);
  const [adminIntakeImportNotice, setAdminIntakeImportNotice] = useState<string | null>(null);
  const [kakaoLookup, setKakaoLookup] = useState<KakaoPlaceLookupState>(initialKakaoLookupState);
  const [stepNotice, setStepNotice] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [missingFields, setMissingFields] = useState<MissingRequiredItem[]>([]);
  const [revealedMissingStepIds, setRevealedMissingStepIds] = useState<Set<string>>(() => new Set());
  const isFormConfigDraftPreview = shouldUseLocalDraftFormConfigPreview();
  const [publishedFormConfig, setPublishedFormConfig] = useState<FormConfigVersion | null>(() => loadAdminDraftFormConfig());
  const [categoryTaxonomy, setCategoryTaxonomy] = useState<CategoryTaxonomyConfig>(DEFAULT_CATEGORY_TAXONOMY_CONFIG);
  const [categoryTaxonomyStatus, setCategoryTaxonomyStatus] = useState<CategoryTaxonomyLoadStatus>("loading");

  const activeFormConfig = publishedFormConfig ?? undefined;
  const activeCategoryTaxonomy = useMemo(() => normalizeCategoryTaxonomyConfig(categoryTaxonomy), [categoryTaxonomy]);
  const visibleCategoryGroups = useMemo(() => (categoryTaxonomyStatus === "ready" ? getVisibleCategoryGroups(activeCategoryTaxonomy) : []), [activeCategoryTaxonomy, categoryTaxonomyStatus]);
  const inviteId = useMemo(() => getInviteIdFromPathname(pathname), [pathname]);
  const routeCategoryId = useMemo(() => (categoryTaxonomyStatus === "ready" && !inviteId ? getCategoryIdFromPathname(pathname, activeCategoryTaxonomy) : ""), [pathname, activeCategoryTaxonomy, categoryTaxonomyStatus, inviteId]);
  const isCategoryFlow = Boolean(routeCategoryId || draft.inviteId);
  const effectiveIntakeSchema = useMemo(() => getEffectiveIntakeSchema(draft.data, activeFormConfig), [draft.data, activeFormConfig]);
  const currentAdminIntakeImportContext = createIntakeImportContext(draft, effectiveIntakeSchema.schemaHash);
  const steps = effectiveIntakeSchema.steps;
  const selectedProfile = getCategoryProfile(draft.data.category.categoryId);
  const selectedCategoryBreadcrumb = draft.data.category.categoryId ? getCategoryDisplayBreadcrumb(draft.data.category.categoryId, activeCategoryTaxonomy) : "";
  const firstStepIndex = isCategoryFlow ? FIRST_SURVEY_STEP_INDEX : 0;
  const boundedCurrentStep = Math.min(Math.max(draft.currentStep, firstStepIndex), steps.length - 1);
  const step = steps[boundedCurrentStep] ?? steps[firstStepIndex] ?? steps[0];
  const currentPartIndex = getPartIndexForStepId(step.id);
  const currentPart = INTAKE_PARTS[currentPartIndex];
  const partSteps = getPartSteps(steps, currentPartIndex).filter((candidate) => candidate.id !== "welcome");
  const progress = Math.round(((currentPartIndex + 1) / INTAKE_PARTS.length) * 100);
  const uploadedCount = draft.files.length;
  const uploadInProgress = Object.values(uploadStatus).some((status) => status === "uploading");
  const saveStatusMessage = getPartnerSaveStatusMessage({
    saveTarget: draft.inviteId ? "invite" : partnerSaveTarget,
    saveStatus,
    uploadInProgress,
    lastSavedLabel: formatDateTime(draft.updatedAt),
  });

  useEffect(() => {
    if (!draft.inviteId) partnerDraftCreatePromiseRef.current = null;
  }, [draft.draftId, draft.inviteId]);

  // 네트워크가 복구되면 서버 초안 생성을 다시 시도해 추가 입력 없이도 draft URL이 잡히게 한다.
  useEffect(() => {
    const retryNow = () => {
      if (latestDraftRef.current.inviteId || partnerDraftCreatePromiseRef.current) return;
      partnerDraftCreateRetryRef.current.attempt = 0;
      setPartnerDraftCreateRetryTick((tick) => tick + 1);
    };
    window.addEventListener("online", retryNow);
    return () => {
      window.removeEventListener("online", retryNow);
      if (partnerDraftCreateRetryRef.current.timeout !== null) window.clearTimeout(partnerDraftCreateRetryRef.current.timeout);
    };
  }, []);

  const schedulePartnerDraftCreateRetry = () => {
    const retry = partnerDraftCreateRetryRef.current;
    if (retry.timeout !== null) window.clearTimeout(retry.timeout);
    const delay = PARTNER_DRAFT_CREATE_RETRY_DELAYS_MS[retry.attempt];
    if (delay === undefined) return;
    retry.attempt += 1;
    retry.timeout = window.setTimeout(() => {
      retry.timeout = null;
      if (latestDraftRef.current.inviteId) return;
      setPartnerDraftCreateRetryTick((tick) => tick + 1);
    }, delay);
  };

  useEffect(() => {
    adminIntakeImportRequestRef.current += 1;
    setAdminIntakeImportLoading(false);
    setAdminIntakeImportProposal(null);
    setAdminIntakeImportError(null);
    setAdminIntakeImportNotice(null);
  }, [currentAdminIntakeImportContext.categoryId, currentAdminIntakeImportContext.draftId]);

  useEffect(() => {
    adminIntakeImportRequestRef.current += 1;
    setAdminIntakeImportLoading(false);
    if (shouldClearAdminIntakeImportNoticeOnSchemaChange(Boolean(adminIntakeImportProposal))) setAdminIntakeImportNotice(null);
    setAdminIntakeImportProposal(null);
  }, [currentAdminIntakeImportContext.schemaHash]);

  useEffect(() => {
    const handlePopState = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    let alive = true;
    if (!isFormConfigDraftPreview) {
      fetchPublishedFormConfig()
        .then((config) => { if (alive) setPublishedFormConfig(config); })
        .catch(() => undefined);
    }
    const handleStorage = (event: StorageEvent) => {
      if (!isFormConfigDraftPreview || event.key !== ADMIN_FORM_CONFIG_DRAFT_STORAGE_KEY) return;
      setPublishedFormConfig(parseStoredFormConfig(event.newValue));
    };
    window.addEventListener("storage", handleStorage);
    return () => {
      alive = false;
      window.removeEventListener("storage", handleStorage);
    };
  }, [isFormConfigDraftPreview]);

  useEffect(() => {
    let alive = true;
    fetchPublishedCategoryTaxonomy()
      .then((config) => {
        if (!alive) return;
        setCategoryTaxonomy(normalizeCategoryTaxonomyConfig(config));
        setCategoryTaxonomyStatus("ready");
      })
      .catch(() => { if (alive) setCategoryTaxonomyStatus("error"); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    fetch("/auth/me", { credentials: "include", headers: { accept: "application/json" } })
      .then(async (response) => {
        if (!alive || !response.ok) return;
        const body = (await response.json().catch(() => null)) as { user?: AdminUser } | null;
        if (body?.user?.email) setAdminUser({ email: body.user.email, name: body.user.name });
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, []);

  const applyCategoryPresetForTaxonomy = (data: DraftState["data"], categoryId: IntakeCategoryId) => {
    const nextData = applyCategoryPreset(data, categoryId, activeFormConfig);
    return categoryId ? { ...nextData, category: { ...nextData.category, label: getCategoryDisplayLabel(categoryId, activeCategoryTaxonomy) } } : nextData;
  };

  const applyLoadedInviteDraft = (nextDraft: DraftState, serverBase?: PartnerServerBase | null) => {
    const categoryId = nextDraft.data.category.categoryId;
    const normalizedDraft = categoryId
      ? {
          ...nextDraft,
          data: applyCategoryPresetForTaxonomy(nextDraft.data, categoryId),
          currentStep: Math.max(nextDraft.currentStep, FIRST_SURVEY_STEP_INDEX),
        }
      : nextDraft;
    activeServerInviteIdRef.current = normalizedDraft.inviteId ?? null;
    if (normalizedDraft.inviteId) setPartnerSaveTarget("invite");
    setPartnerServerBase(normalizedDraft.inviteId ?? null, normalizedDraft.inviteId ? (serverBase ?? partnerServerBaseFromDraft(normalizedDraft)) : null);
    storePartnerDraftLocally(normalizedDraft);
    setDraft(normalizedDraft);
    setShowResumePrompt(false);
    setInviteBootstrap({ status: "idle" });
    if (normalizedDraft.inviteId) {
      window.history.replaceState(null, "", `/invite/${encodeURIComponent(normalizedDraft.inviteId)}`);
    }
    setPathname(window.location.pathname);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const setPartnerServerBase = (inviteIdForBase: string | null | undefined, base: PartnerServerBase | null) => {
    partnerServerBaseRef.current = base;
    if (inviteIdForBase && base) storePartnerServerBaseLocally(inviteIdForBase, base);
  };

  const partnerBaseFingerprint = () => (partnerServerBaseRef.current ? partnerDraftFingerprint(partnerServerBaseRef.current) : null);

  const reportCoeditConflicts = (paths: string[]) => {
    const sections = [...new Set(paths.map((path) => COEDIT_SECTION_LABELS[path.split(".")[0]] ?? path))];
    setCoeditNotice({ kind: "conflict", message: `같은 항목(${sections.join(", ")})이 다른 곳에서도 수정되어 이 브라우저의 입력을 유지했어요.` });
  };

  const reportRemoteDraftUpdate = () => {
    setCoeditNotice({ kind: "updated", message: "다른 참여자의 변경을 반영했어요. 현재 작성 중인 내용은 그대로 유지됩니다." });
  };

  // 서버 최신본을 base/local과 3-way 병합해 화면과 저장 시도 초안에 반영한다.
  const mergeRemoteInviteDraft = (invite: PartnerInvite, attempt: DraftState, baseOverride?: PartnerServerBase | null): DraftState | null => {
    const remoteDraft = createDraftFromInvite(invite, { config: activeFormConfig });
    if (!remoteDraft) return null;
    const base = baseOverride ?? partnerServerBaseRef.current;
    // 기준본이 없으면 콘텐츠는 로컬 우선(base=remote), 파일은 빈 base 합집합으로 병합해 원격 파일을 드롭하지 않는다.
    const baseData = base?.data ?? remoteDraft.data;
    const baseFiles = base?.files ?? [];
    const mergeAgainst = (local: DraftState): { draft: DraftState; conflictPaths: string[] } => {
      const mergedData = mergeIntakeData(baseData, local.data, remoteDraft.data);
      const mergedFiles = mergeUploadedFiles(baseFiles, local.files, remoteDraft.files);
      return {
        conflictPaths: mergedData.conflictPaths,
        draft: {
          ...local,
          // 입력 중인 임시 비밀번호는 서버 응답에서 항상 비워지므로 원격 값으로 덮어쓰지 않는다.
          data: { ...mergedData.data, reservation: { ...mergedData.data.reservation, adminPassword: local.data.reservation.adminPassword } },
          files: mergedFiles,
          draftVersion: remoteDraft.draftVersion,
          revisionReason: remoteDraft.revisionReason,
          hasSavedAdminPassword: remoteDraft.hasSavedAdminPassword,
          updatedAt: remoteDraft.updatedAt,
        },
      };
    };
    const merged = mergeAgainst(attempt);
    if (merged.conflictPaths.length) reportCoeditConflicts(merged.conflictPaths);
    else reportRemoteDraftUpdate();
    setPartnerServerBase(remoteDraft.inviteId ?? invite.id, partnerServerBaseFromDraft(remoteDraft));
    setDraft((current) => {
      const next = current === attempt ? merged.draft : mergeAgainst(current).draft;
      latestDraftRef.current = next;
      storePartnerDraftLocally(next);
      return next;
    });
    return merged.draft;
  };

  // 409 복구용 최신본 조회. 배포 스큐로 sync 라우트가 없으면(404) 기존 초대 조회로 1회 폴백한다.
  const fetchLatestInviteForRecovery = async (inviteIdForSync: string): Promise<{ invite: PartnerInvite } | { blocked: PartnerResolvedSave }> => {
    const sync = await fetchPartnerDraftSync(inviteIdForSync);
    if (sync.status === "changed") return { invite: sync.invite };
    if (sync.status === "submitted") return { blocked: { status: "submitted" } };
    if (sync.status === "expired") return { blocked: { status: "expired" } };
    if (sync.status === "notFound") {
      const legacy = await fetchPartnerInvite(inviteIdForSync);
      if (legacy.status === "success") return { invite: legacy.invite };
      if (legacy.status === "submitted") return { blocked: { status: "submitted" } };
      if (legacy.status === "expired") return { blocked: { status: "expired" } };
      return { blocked: { status: "error", message: legacy.status === "error" ? legacy.message : "초안 동기화에 실패했습니다." } };
    }
    return { blocked: { status: "error", message: sync.status === "error" ? sync.message : "초안 동기화에 실패했습니다." } };
  };

  // 파일 업로드/삭제가 버전 충돌로 실패했을 때 최신본을 병합하고 새 버전을 돌려준다.
  const resyncPartnerDraftVersion = async (): Promise<{ draftVersion: number } | null> => {
    const latest = latestDraftRef.current;
    if (!latest.inviteId) return null;
    const result = await fetchLatestInviteForRecovery(latest.inviteId);
    if ("blocked" in result) {
      if (result.blocked.status === "submitted") setInviteBootstrap({ status: "submitted" });
      else if (result.blocked.status === "expired") setInviteBootstrap({ status: "expired" });
      return null;
    }
    const merged = mergeRemoteInviteDraft(result.invite, latestDraftRef.current);
    return merged ? { draftVersion: merged.draftVersion ?? 0 } : null;
  };

  // CAS 저장 + 409 시 최신본 병합 후 제한된 횟수만 재시도한다.
  const performPartnerDraftSave = async (initialDraft: DraftState): Promise<PartnerResolvedSave> => {
    let attempt = initialDraft;
    // 재시도 중 폴링이 공유 base를 옮겨도 이 시도는 시작 시점의 base로 병합해야 원격 변경을 잃지 않는다.
    let attemptBase = partnerServerBaseRef.current;
    for (let attemptIndex = 0; attemptIndex < PARTNER_SAVE_MAX_ATTEMPTS; attemptIndex += 1) {
      const result = await autosavePartnerDraft(attempt);
      if (result.status === "success") {
        return { status: "success", draftVersion: result.draftVersion, updatedAt: result.updatedAt, hasSavedAdminPassword: result.hasSavedAdminPassword, savedData: attempt.data, savedFiles: attempt.files };
      }
      if (result.status === "submitted" || result.status === "expired") return { status: result.status };
      if (result.status === "error") return result;
      const latestInvite = await fetchLatestInviteForRecovery(attempt.inviteId!);
      if ("blocked" in latestInvite) return latestInvite.blocked;
      const merged = mergeRemoteInviteDraft(latestInvite.invite, attempt, attemptBase);
      if (!merged) return { status: "error", message: "초대 데이터 형식이 올바르지 않습니다." };
      attempt = merged;
      attemptBase = partnerServerBaseRef.current;
    }
    return { status: "error", message: PARTNER_DRAFT_CONFLICT_MESSAGE };
  };

  const runPartnerAutosave = (sourceDraft: DraftState): Promise<PartnerResolvedSave> => {
    const existing = partnerAutosaveRef.current;
    if (existing) return existing.promise;
    const promise = performPartnerDraftSave(sourceDraft);
    const operation = { promise };
    partnerAutosaveRef.current = operation;
    void promise
      .finally(() => {
        if (partnerAutosaveRef.current === operation) partnerAutosaveRef.current = null;
      })
      .catch(() => undefined);
    return promise;
  };

  const applyPartnerSaveSuccess = (saved: Extract<PartnerResolvedSave, { status: "success" }>, inviteIdForSave: string) => {
    setPartnerServerBase(inviteIdForSave, { draftVersion: saved.draftVersion, data: saved.savedData, files: saved.savedFiles });
    setDraft((current) => {
      const next = { ...current, draftVersion: saved.draftVersion, updatedAt: saved.updatedAt, hasSavedAdminPassword: saved.hasSavedAdminPassword };
      latestDraftRef.current = next;
      storePartnerDraftLocally(next);
      return next;
    });
    setSubmitError((current) => (current === PARTNER_DRAFT_CONFLICT_MESSAGE ? null : current));
    setSaveStatus("saved");
  };

  // 이동(다음/이전)이나 명시적 저장 시점에 변경분을 즉시 서버에 반영한다.
  const flushPartnerDraftNow = (): Promise<void> => {
    const latest = latestDraftRef.current;
    if (!latest.inviteId || latest.submittedAt || partnerMutationQueueRef.current.hasPending()) return Promise.resolve();
    if (partnerAutosaveRef.current) {
      return partnerAutosaveRef.current.promise.then((result) => (
        result.status === "success" && partnerBaseFingerprint() !== partnerDraftFingerprint(latestDraftRef.current)
          ? flushPartnerDraftNow()
          : undefined
      ), () => undefined);
    }
    if (partnerBaseFingerprint() === partnerDraftFingerprint(latest)) return Promise.resolve();
    if (partnerAutosaveTimeoutRef.current !== null) {
      window.clearTimeout(partnerAutosaveTimeoutRef.current);
      partnerAutosaveTimeoutRef.current = null;
    }
    setSaveStatus("saving");
    const inviteIdForSave = latest.inviteId;
    return runPartnerAutosave(latest)
      .then((result) => {
        if (result.status === "success") applyPartnerSaveSuccess(result, inviteIdForSave);
        else if (result.status === "submitted") setInviteBootstrap({ status: "submitted" });
        else if (result.status === "expired") setInviteBootstrap({ status: "expired" });
        else {
          setSubmitError(result.message);
          setSaveStatus("error");
        }
      })
      .catch(() => setSaveStatus("error"));
  };

  useEffect(() => {
    if (!inviteId || categoryTaxonomyStatus !== "ready") return;
    if (activeServerInviteIdRef.current === inviteId) return;
    let alive = true;
    setInviteBootstrap({ status: "loading" });
    fetchPartnerInvite(inviteId).then((result) => {
      if (!alive) return;
      if (result.status !== "success") {
        setInviteBootstrap(result.status === "error" ? { status: "error", message: result.message } : { status: result.status });
        return;
      }
      const inviteDraft = createDraftFromInvite(result.invite, { config: activeFormConfig });
      if (!inviteDraft) {
        setInviteBootstrap({ status: "error", message: "초대 데이터 형식이 올바르지 않습니다." });
        return;
      }
      const stored = loadStoredDraft();
      const remoteBase = partnerServerBaseFromDraft(inviteDraft);
      if (hasMeaningfulInviteLocalDraft(stored)) {
        if (stored.draftId === inviteDraft.draftId) {
          // 로컬 진행본을 서버 최신본과 3-way 병합한다. 기준본이 없는 기존 저장분은 콘텐츠는 로컬 우선,
          // 파일은 빈 base 합집합으로 병합한다(base=remote면 서버 전용 파일을 로컬 삭제로 오판해 드롭된다).
          const storedBase = inviteDraft.inviteId ? loadStoredPartnerServerBase(inviteDraft.inviteId) : null;
          const contentBase = storedBase ?? remoteBase;
          const mergedData = mergeIntakeData(contentBase.data, stored.data, inviteDraft.data);
          const mergedFiles = mergeUploadedFiles(storedBase ? storedBase.files : [], stored.files, inviteDraft.files);
          if (mergedData.conflictPaths.length) reportCoeditConflicts(mergedData.conflictPaths);
          applyLoadedInviteDraft({
            ...stored,
            inviteId: inviteDraft.inviteId,
            draftVersion: inviteDraft.draftVersion,
            revisionReason: inviteDraft.revisionReason,
            hasSavedAdminPassword: inviteDraft.hasSavedAdminPassword,
            data: { ...mergedData.data, reservation: { ...mergedData.data.reservation, adminPassword: stored.data.reservation.adminPassword } },
            files: mergedFiles,
            updatedAt: inviteDraft.updatedAt,
          }, remoteBase);
          return;
        }
        setDraft(stored);
        setInviteBootstrap({ status: "conflict", draft: inviteDraft });
        return;
      }
      applyLoadedInviteDraft(inviteDraft, remoteBase);
    });
    return () => { alive = false; };
  }, [inviteId, categoryTaxonomyStatus, activeFormConfig, activeCategoryTaxonomy, draft.inviteId]);

  useEffect(() => {
    if (!routeCategoryId || showResumePrompt) return;
    setDraft((current) => {
      const baseDraft = current.submittedAt ? createInitialDraft() : current;
      const needsCategory = baseDraft.data.category.categoryId !== routeCategoryId;
      const needsStep = baseDraft.currentStep < FIRST_SURVEY_STEP_INDEX;
      if (baseDraft === current && !needsCategory && !needsStep) return current;
      return {
        ...baseDraft,
        data: needsCategory ? applyCategoryPresetForTaxonomy(baseDraft.data, routeCategoryId) : baseDraft.data,
        currentStep: Math.max(baseDraft.currentStep, FIRST_SURVEY_STEP_INDEX),
      };
    });
  }, [routeCategoryId, showResumePrompt, activeFormConfig, activeCategoryTaxonomy]);

  useEffect(() => {
    setDraft((current) => (
      current.data.category.categoryId
        ? { ...current, data: applyCategoryPresetForTaxonomy(current.data, current.data.category.categoryId) }
        : current
    ));
  }, [publishedFormConfig, activeCategoryTaxonomy]);

  useEffect(() => {
    if (draft.submittedAt) return;
    if (inviteId && activeServerInviteIdRef.current !== inviteId) return;
    // 초대 링크의 입력은 서버 반영을 기다리지 않고 이 브라우저에 먼저 보관한다.
    if (draft.inviteId) storePartnerDraftLocally(draft);
    const categoryId = draft.data.category.categoryId;
    const initialData = categoryId ? applyCategoryPresetForTaxonomy(createDefaultIntakeData(), categoryId) : createDefaultIntakeData();
    if (!draft.inviteId && isCategoryFlow && !showResumePrompt && hasMeaningfulPartnerEdit(draft, initialData)) {
      // 서버 초안 생성이 실패해도 이 브라우저에는 입력이 남아야 한다.
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(draft));
      } catch {
        // 저장소를 쓸 수 없는 환경은 서버 초안 생성으로만 보존한다.
      }
      if (!partnerDraftCreatePromiseRef.current) {
        setPartnerSaveTarget("inviteCreation");
        setSaveStatus("saving");
        const requestedDraft = draft;
        partnerDraftCreatePromiseRef.current = createPartnerDraft(requestedDraft)
          .then((invite) => {
            partnerDraftCreateRetryRef.current.attempt = 0;
            // 재시도로 생성이 성공했으니 이전 생성 실패 배너만 거둑다.
            const staleError = partnerDraftCreateErrorRef.current;
            partnerDraftCreateErrorRef.current = null;
            if (staleError !== null) setSubmitError((current) => (current === staleError ? null : current));
            return applyCreatedPartnerDraft(invite, requestedDraft);
          })
          .catch((error) => {
            partnerDraftCreatePromiseRef.current = null;
            if (isPartnerDraftCapabilityConflictError(error) && requestedDraft.files.length === 0 && !partnerDraftIdRecoveredRef.current) {
              partnerDraftIdRecoveredRef.current = true;
              // 이 브라우저가 기존 draft의 생성 권한을 잃었으므로 새 draft 실이로 이어서 진행한다.
              setDraft((current) => (current.inviteId ? current : { ...current, draftId: createDraftId() }));
              return null;
            }
            const message = error instanceof Error ? error.message : "초대 초안을 만들지 못했습니다.";
            partnerDraftCreateErrorRef.current = message;
            setSaveStatus("error");
            setSubmitError(message);
            schedulePartnerDraftCreateRetry();
            return null;
          });
      }
      return;
    }

    if (draft.inviteId && partnerBaseFingerprint() === partnerDraftFingerprint(draft)) {
      setSaveStatus("saved");
      return;
    }

    setSaveStatus("saving");
    const timeout = window.setTimeout(async () => {
      partnerAutosaveTimeoutRef.current = null;
      try {
        if (draft.inviteId) {
          const sourceDraft = latestDraftRef.current;
          // 서버 반영 성공 여부와 무관하게 입력본을 로컬에 먼저 보관한다.
          storePartnerDraftLocally(sourceDraft);
          if (partnerMutationQueueRef.current.hasPending()) return;
          if (partnerBaseFingerprint() === partnerDraftFingerprint(sourceDraft)) {
            setSaveStatus("saved");
            return;
          }
          if (partnerAutosaveRef.current) return;
          const result = await runPartnerAutosave(sourceDraft);
          if (result.status === "success") {
            applyPartnerSaveSuccess(result, sourceDraft.inviteId ?? draft.inviteId);
            return;
          }
          if (result.status === "submitted") setInviteBootstrap({ status: "submitted" });
          else if (result.status === "expired") setInviteBootstrap({ status: "expired" });
          else setSubmitError(result.message);
          setSaveStatus("error");
          return;
        }
        const next = { ...draft, updatedAt: nowIso() };
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(next));
        setDraft((current) => (current.updatedAt === next.updatedAt ? current : next));
        setSaveStatus("saved");
      } catch (error) {
        setSaveStatus("error");
        setSubmitError(error instanceof Error ? error.message : "자동 저장에 실패했습니다.");
      }
    }, draft.inviteId ? PARTNER_AUTOSAVE_DEBOUNCE_MS : 450);
    partnerAutosaveTimeoutRef.current = timeout;
    return () => {
      window.clearTimeout(timeout);
      if (partnerAutosaveTimeoutRef.current === timeout) partnerAutosaveTimeoutRef.current = null;
    };
  }, [draft.data, draft.files, draft.currentStep, draft.draftVersion, draft.submissionId, draft.submittedAt, draft.inviteId, draft.draftId, inviteId, isCategoryFlow, showResumePrompt, activeFormConfig, partnerDraftCreateRetryTick]);

  // 주소창은 항상 현재 서버 초안을 가리킨다(다른 경로에서 되돌려도 복구).
  useEffect(() => {
    if (!draft.inviteId || draft.submittedAt) return;
    if (activeServerInviteIdRef.current !== draft.inviteId) return;
    if (getInviteIdFromPathname(pathname) === draft.inviteId) return;
    window.history.replaceState(null, "", `/invite/${encodeURIComponent(draft.inviteId)}`);
    setPathname(window.location.pathname);
  }, [draft.inviteId, draft.submittedAt, pathname]);

  // 다른 브라우저의 변경을 주기적으로 받아 편집 중이지 않은 필드에 반영한다.
  useEffect(() => {
    if (!draft.inviteId || draft.submittedAt) return;
    const inviteIdForSync = draft.inviteId;
    const interval = window.setInterval(async () => {
      if (document.visibilityState !== "visible") return;
      if (partnerSyncInFlightRef.current || partnerAutosaveRef.current || partnerMutationQueueRef.current.hasPending()) return;
      partnerSyncInFlightRef.current = true;
      try {
        const result = await fetchPartnerDraftSync(inviteIdForSync, latestDraftRef.current.draftVersion ?? 0);
        if (result.status === "changed") mergeRemoteInviteDraft(result.invite, latestDraftRef.current);
        else if (result.status === "submitted") setInviteBootstrap({ status: "submitted" });
        else if (result.status === "expired") setInviteBootstrap({ status: "expired" });
        // unchanged는 정상, notFound/error는 일시 장애일 수 있으므로 폴링에서는 조용히 다음 주기를 기다린다.
      } finally {
        partnerSyncInFlightRef.current = false;
      }
    }, PARTNER_DRAFT_SYNC_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [draft.inviteId, draft.submittedAt, activeFormConfig]);

  useEffect(() => {
    const target = pendingMissingTargetRef.current;
    if (!target) return;
    const targetStepId = steps[target.stepIndex]?.id ?? target.stepId;
    if (getPartIndexForStepId(targetStepId) !== currentPartIndex) return;
    pendingMissingTargetRef.current = null;
    window.requestAnimationFrame(() => scrollToMissingTarget(target));
  }, [boundedCurrentStep, step.id, currentPartIndex, steps]);

  const partCompletions = useMemo(() => INTAKE_PARTS.map((_, partIndex) => getPartSteps(steps, partIndex).reduce(
    (acc, candidate) => {
      const completion = getStepCompletion(candidate, draft);
      return { done: acc.done + completion.done, total: acc.total + completion.total };
    },
    { done: 0, total: 0 },
  )), [draft, steps]);

  const resumeStoredDraft = () => {
    const storedCategoryId = draft.data.category.categoryId;
    if (storedCategoryId) {
      // 이어서 쓰는 초안이 이밌 서버에 있으면 초안 URL을 지킨다.
      const storedPath = draft.inviteId ? `/invite/${encodeURIComponent(draft.inviteId)}` : getCategoryPath(storedCategoryId, activeCategoryTaxonomy);
      window.history.replaceState(null, "", storedPath);
      setPathname(window.location.pathname);
    } else {
      setDraft((current) => ({
        ...current,
        data: routeCategoryId ? applyCategoryPresetForTaxonomy(current.data, routeCategoryId) : current.data,
        currentStep: routeCategoryId ? FIRST_SURVEY_STEP_INDEX : 0,
      }));
    }
    setShowResumePrompt(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navigateToCategory = (categoryId: IntakeCategoryId) => {
    const nextPath = getCategoryPath(categoryId, activeCategoryTaxonomy);
    if (typeof window !== "undefined") {
      window.history.pushState(null, "", nextPath);
      setPathname(window.location.pathname);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    setSubmitError(null);
    setStepNotice(null);
    setShowResumePrompt(false);
    setDraft((current) => {
      const baseDraft = current.submittedAt ? createInitialDraft() : current;
      return {
        ...baseDraft,
        data: applyCategoryPresetForTaxonomy(baseDraft.data, categoryId),
        currentStep: FIRST_SURVEY_STEP_INDEX,
      };
    });
  };

  const updateField = (field: IntakeField, rawValue: string | boolean) => {
    if (field.readOnly) return;
    setDraft((current) => {
      let nextData = setPathValue(current.data, field.path, rawValue);
      const useEmail = getPathValue(nextData, "reservation.useContactEmailForNotifications") === true;
      const usePhone = getPathValue(nextData, "reservation.useContactPhoneForAlimtalk") === true;
      const useTaxInvoiceContact = getPathValue(nextData, "company.useContactInfoForTaxInvoice") === true;
      if (useEmail && ["company.contactEmail", "reservation.useContactEmailForNotifications"].includes(field.path)) {
        nextData = setPathValue(nextData, "reservation.notificationEmails", fieldValueToString(getPathValue(nextData, "company.contactEmail")));
      }
      if (usePhone && ["company.contactPhone", "reservation.useContactPhoneForAlimtalk"].includes(field.path)) {
        nextData = setPathValue(nextData, "reservation.notificationPhones", fieldValueToString(getPathValue(nextData, "company.contactPhone")));
      }
      if (useTaxInvoiceContact && ["company.contactName", "company.contactPhone", "company.contactEmail", "company.useContactInfoForTaxInvoice"].includes(field.path)) {
        nextData = setPathValue(nextData, "company.taxInvoiceContactName", fieldValueToString(getPathValue(nextData, "company.contactName")));
        nextData = setPathValue(nextData, "company.taxInvoiceContactPhone", fieldValueToString(getPathValue(nextData, "company.contactPhone")));
        nextData = setPathValue(nextData, "company.taxInvoiceContactEmail", fieldValueToString(getPathValue(nextData, "company.contactEmail")));
      }
      if (field.path === "content.sellingPointTags" && !parseMultiCheckboxValue(rawValue).includes("관광객 방문 편의")) {
        nextData = setPathValue(nextData, "content.touristConvenienceOptions", "");
      }
      if (field.path === "content.targetCustomers" && !parseMultiCheckboxValue(rawValue).includes("특정 고민이 있는 고객")) {
        nextData = setPathValue(nextData, "content.targetCustomerConcernDetail", "");
      }
      if (field.path === "content.creatripExclusiveBenefitStatus" && rawValue !== "있음") {
        nextData = setPathValue(nextData, "content.creatripExclusiveBenefit", "");
      }
      return { ...current, data: nextData };
    });
  };

  const updateRepeaterItem = (repeater: RepeaterField, index: number, itemPath: string, value: string | boolean) => {
    setDraft((current) => {
      const items = getPathValue(current.data, repeater.path);
      if (!Array.isArray(items)) return current;
      const nextItems = items.map((item, itemIndex) => (itemIndex === index ? { ...(item as Record<string, unknown>), [itemPath]: value } : item));
      return { ...current, data: setPathValue(current.data, repeater.path, nextItems) };
    });
  };

  const addRepeaterItem = (repeater: RepeaterField) => {
    setDraft((current) => {
      const items = getPathValue(current.data, repeater.path);
      const nextItems = Array.isArray(items) ? [...items, repeater.createItem()] : [repeater.createItem()];
      return { ...current, data: setPathValue(current.data, repeater.path, nextItems) };
    });
  };

  const removeRepeaterItem = (repeater: RepeaterField, index: number) => {
    setDraft((current) => {
      const items = getPathValue(current.data, repeater.path);
      if (!Array.isArray(items)) return current;
      if (repeater.minItems && items.length <= repeater.minItems) return current;
      const nextItems = items.filter((_, itemIndex) => itemIndex !== index);
      const nextFiles = repeater.id === "doctors"
        ? reindexDoctorPhotoFilesAfterRemoval(current.files, index)
        : repeater.id === "products"
          ? reindexProductDetailPhotoFilesAfterRemoval(current.files, index)
          : current.files;
      return { ...current, data: setPathValue(current.data, repeater.path, nextItems), files: nextFiles };
    });
  };

  const replaceRepeaterItems = (repeater: RepeaterField, items: Record<string, unknown>[]) => {
    setDraft((current) => ({ ...current, data: setPathValue(current.data, repeater.path, items) }));
  };

  const applyCreatedPartnerDraft = (invite: PartnerInvite, requestedDraft: DraftState): { inviteId: string; draftVersion: number } => {
    const inviteDraft = createDraftFromInvite(invite, { config: activeFormConfig });
    if (!inviteDraft) throw new Error("초대 초안 응답 형식이 올바르지 않습니다.");
    setDraft((current) => {
      const next = {
        ...current,
        inviteId: inviteDraft.inviteId,
        draftVersion: inviteDraft.draftVersion,
        revisionReason: inviteDraft.revisionReason,
        hasSavedAdminPassword: inviteDraft.hasSavedAdminPassword,
        updatedAt: inviteDraft.updatedAt,
      };
      activeServerInviteIdRef.current = invite.id;
      latestDraftRef.current = next;
      return next;
    });
    setPartnerServerBase(invite.id, { draftVersion: inviteDraft.draftVersion ?? 0, data: requestedDraft.data, files: requestedDraft.files });
    window.history.replaceState(null, "", `/invite/${encodeURIComponent(invite.id)}`);
    setPathname(window.location.pathname);
    setPartnerSaveTarget("invite");
    setSaveStatus("saved");
    return { inviteId: invite.id, draftVersion: inviteDraft.draftVersion ?? 0 };
  };

  const preparePartnerMutation = async (): Promise<DraftState> => {
    if (partnerAutosaveTimeoutRef.current !== null) {
      window.clearTimeout(partnerAutosaveTimeoutRef.current);
      partnerAutosaveTimeoutRef.current = null;
    }

    const sourceDraft = latestDraftRef.current;
    if (!sourceDraft.inviteId) return sourceDraft;
    if (!partnerAutosaveRef.current && partnerBaseFingerprint() === partnerDraftFingerprint(sourceDraft)) return sourceDraft;

    const result = await runPartnerAutosave(sourceDraft);
    if (result.status !== "success") {
      if (result.status === "submitted") setInviteBootstrap({ status: "submitted" });
      else if (result.status === "expired") setInviteBootstrap({ status: "expired" });
      throw new Error(result.status === "error" ? result.message : "초안 상태를 갱신하지 못했습니다.");
    }

    applyPartnerSaveSuccess(result, sourceDraft.inviteId);
    return { ...latestDraftRef.current, draftVersion: result.draftVersion, updatedAt: result.updatedAt, hasSavedAdminPassword: result.hasSavedAdminPassword };
  };

  const runPartnerMutation = <T,>(mutation: () => Promise<T>): Promise<T> => partnerMutationQueueRef.current.run(mutation);

  const ensurePartnerInviteForMutation = async (sourceDraft: DraftState = draft): Promise<{ inviteId: string; draftVersion: number } | null> => {
    if (sourceDraft.inviteId) return { inviteId: sourceDraft.inviteId, draftVersion: sourceDraft.draftVersion ?? 0 };
    if (!isCategoryFlow) return null;
    if (!partnerDraftCreatePromiseRef.current) {
      setPartnerSaveTarget("inviteCreation");
      setSaveStatus("saving");
      partnerDraftCreatePromiseRef.current = createPartnerDraft(sourceDraft)
        .then((invite) => applyCreatedPartnerDraft(invite, sourceDraft))
        .catch((error) => {
          partnerDraftCreatePromiseRef.current = null;
          setSaveStatus("error");
          throw error;
        });
    }
    return partnerDraftCreatePromiseRef.current;
  };

  const uploadFiles = async (definition: FileUploadDefinition, fileList: FileList | null) => {
    if (!fileList?.length) return;
    const selectedFiles = Array.from(fileList);
    const existingCount = draft.files.filter((file) => isUploadedFileForDefinition(file, definition)).length;
    const selectionFailure = validateUploadSelection(definition, existingCount, selectedFiles);
    if (selectionFailure) {
      setUploadStatus((current) => ({ ...current, [definition.id]: selectionFailure }));
      return;
    }
    setUploadStatus((current) => ({ ...current, [definition.id]: "uploading" }));
    let failedFile: File | undefined;
    try {
      await runPartnerMutation(async () => {
        const mutationDraft = await preparePartnerMutation();
        const capability = await ensurePartnerInviteForMutation(mutationDraft);
        let nextDraftVersion = capability?.draftVersion;
        for (const file of selectedFiles) {
          failedFile = file;
          let result: Awaited<ReturnType<typeof uploadPartnerFile>>;
          try {
            result = await uploadPartnerFile(capability?.inviteId, nextDraftVersion, file, definition.category, definition.id);
          } catch (error) {
            // 다른 곳의 저장과 겹친 버전 충돌이면 최신본을 병합한 뒤 1회 재시도한다.
            const refreshed = isPartnerDraftVersionConflictError(error) ? await resyncPartnerDraftVersion() : null;
            if (!refreshed) throw error;
            nextDraftVersion = refreshed.draftVersion;
            result = await uploadPartnerFile(capability?.inviteId, nextDraftVersion, file, definition.category, definition.id);
          }
          nextDraftVersion = result.draftVersion;
          latestDraftRef.current = { ...latestDraftRef.current, draftVersion: result.draftVersion, files: [...latestDraftRef.current.files, result.file] };
          setDraft((current) => ({ ...current, draftVersion: result.draftVersion, files: [...current.files, result.file] }));
        }
      });
      setUploadStatus((current) => ({ ...current, [definition.id]: "done" }));
    } catch (error) {
      setUploadStatus((current) => ({ ...current, [definition.id]: describeUploadFailure(error, failedFile, definition.accept) }));
    }
  };

  const removeUploadedFile = async (fileId: string) => {
    const initialTargetFile = draft.files.find((file) => file.id === fileId);
    if (!initialTargetFile || initialTargetFile.readOnly) return;

    try {
      await runPartnerMutation(async () => {
        const mutationDraft = await preparePartnerMutation();
        const targetFile = mutationDraft.files.find((file) => file.id === fileId) ?? initialTargetFile;
        if (mutationDraft.inviteId) {
          let nextDraftVersion: number;
          try {
            nextDraftVersion = await deletePartnerFile(mutationDraft.inviteId, mutationDraft.draftVersion, targetFile);
          } catch (error) {
            // 다른 곳의 저장과 겹친 버전 충돌이면 최신본을 병합한 뒤 1회 재시도한다.
            const refreshed = isPartnerDraftVersionConflictError(error) ? await resyncPartnerDraftVersion() : null;
            if (!refreshed) throw error;
            nextDraftVersion = await deletePartnerFile(mutationDraft.inviteId, refreshed.draftVersion, targetFile);
          }
          latestDraftRef.current = { ...latestDraftRef.current, draftVersion: nextDraftVersion, files: latestDraftRef.current.files.filter((file) => file.id !== fileId) };
          setDraft((current) => ({ ...current, draftVersion: nextDraftVersion, files: current.files.filter((file) => file.id !== fileId) }));
          return;
        }
        latestDraftRef.current = { ...latestDraftRef.current, files: latestDraftRef.current.files.filter((file) => file.id !== fileId) };
        setDraft((current) => ({ ...current, files: current.files.filter((file) => file.id !== fileId) }));
        const deletePath = targetFile.deletePath ?? (targetFile.downloadPath.startsWith("/api/") ? targetFile.downloadPath : "");
        if (!deletePath) return;
        const response = await fetch(deletePath, { method: "DELETE" });
        if (!response.ok) throw new Error("파일 삭제에 실패했습니다.");
      });
    } catch (error) {
      if (!draft.inviteId) {
        if (!latestDraftRef.current.files.some((file) => file.id === fileId)) latestDraftRef.current = { ...latestDraftRef.current, files: [...latestDraftRef.current.files, initialTargetFile] };
        setDraft((current) => (current.files.some((file) => file.id === fileId) ? current : { ...current, files: [...current.files, initialTargetFile] }));
      }
      setSubmitError(error instanceof Error ? error.message : "파일 삭제에 실패했습니다.");
    }
  };

  const renameUploadedFile = (fileId: string, nextFileName: string) => {
    setDraft((current) => ({
      ...current,
      files: current.files.map((file) => (file.id === fileId ? { ...file, fileName: nextFileName } : file)),
    }));
  };

  const reorderUploadedFile = (definition: FileUploadDefinition, draggedFileId: string, targetFileId: string) => {
    setDraft((current) => {
      if (draggedFileId === targetFileId) return current;
      const scopedFiles = current.files.filter((file) => isUploadedFileForDefinition(file, definition));
      const fromIndex = scopedFiles.findIndex((file) => file.id === draggedFileId);
      const toIndex = scopedFiles.findIndex((file) => file.id === targetFileId);
      if (fromIndex < 0 || toIndex < 0) return current;

      const reorderedScopedFiles = [...scopedFiles];
      const [movedFile] = reorderedScopedFiles.splice(fromIndex, 1);
      reorderedScopedFiles.splice(toIndex, 0, movedFile);
      let cursor = 0;
      const nextFiles = current.files.map((file) => (isUploadedFileForDefinition(file, definition) ? reorderedScopedFiles[cursor++] : file));
      return { ...current, files: nextFiles };
    });
  };

  const revealRequiredValidation = (navigation: RequiredValidationNavigation) => {
    setRevealedMissingStepIds((current) => updateRevealedMissingStepIds(current, navigation));
  };

  const goToStep = (nextStep: number) => {
    const targetStepIndex = Math.min(Math.max(nextStep, firstStepIndex), steps.length - 1);
    const targetStep = steps[targetStepIndex];
    if (targetStep && targetStepIndex < boundedCurrentStep) revealRequiredValidation({ kind: "return", stepId: targetStep.id });
    setSubmitError(null);
    setMissingFields([]);
    setStepNotice(null);
    setCoeditNotice(null);
    const hasPendingPartnerChanges = Boolean(
      latestDraftRef.current.inviteId
      && partnerBaseFingerprint() !== partnerDraftFingerprint(latestDraftRef.current),
    );
    setDraft((current) => ({ ...current, currentStep: targetStepIndex }));
    if (hasPendingPartnerChanges) setIsStepTransitionSaving(true);
    void flushPartnerDraftNow().finally(() => setIsStepTransitionSaving(false));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goToMissingField = (item: MissingRequiredItem) => {
    if (item.kind === "category") {
      window.history.pushState(null, "", "/");
      setPathname("/");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    pendingMissingTargetRef.current = item;
    revealRequiredValidation({ kind: "missingJump", stepId: item.stepId });
    goToStep(item.stepIndex);
    setStepNotice(`${item.stepTitle}의 '${item.label}' 위치로 이동했어요. 표시된 칸을 채워주세요.`);
    if (getPartIndexForStepId(item.stepId) === currentPartIndex) {
      window.requestAnimationFrame(() => scrollToMissingTarget(item));
      pendingMissingTargetRef.current = null;
    }
  };

  const goToPart = (partIndex: number) => {
    const bounded = Math.min(Math.max(partIndex, 0), INTAKE_PARTS.length - 1);
    if (bounded < currentPartIndex) {
      for (const candidate of getPartSteps(steps, bounded)) {
        revealRequiredValidation({ kind: "return", stepId: candidate.id });
      }
    }
    goToStep(Math.max(getFirstStepIndexOfPart(steps, bounded), firstStepIndex));
  };

  const goNextPartWithCheck = () => {
    const missingBySection = partSteps.map((candidate) => ({ stepId: candidate.id, missing: collectMissingRequiredForStep(candidate, draft) }));
    missingBySection.forEach(({ stepId, missing }) => revealRequiredValidation({ kind: "next", stepId, missingCount: missing.length }));
    const missing = missingBySection.flatMap((entry) => entry.missing);
    const notice = missing.length > 0
      ? `이전 파트에 아직 ${missing.length}개 필수 항목이 남아 있어요. 제출 전에만 채우면 됩니다: ${missing.slice(0, 4).join(", ")}${missing.length > 4 ? ` 외 ${missing.length - 4}개` : ""}`
      : null;
    goToPart(currentPartIndex + 1);
    setStepNotice(notice);
  };

  const startNewDraft = () => {
    const base = createInitialDraft();
    const nextCategoryId = routeCategoryId || draft.data.category.categoryId;
    const next = nextCategoryId
      ? { ...base, data: applyCategoryPresetForTaxonomy(base.data, nextCategoryId), currentStep: FIRST_SURVEY_STEP_INDEX }
      : base;
    // 이전 초안 URL에 새 초안이 밀려들어가지 않도록 경로를 되돌린다.
    if (getInviteIdFromPathname(window.location.pathname)) {
      window.history.replaceState(null, "", nextCategoryId ? getCategoryPath(nextCategoryId, activeCategoryTaxonomy) : "/");
      setPathname(window.location.pathname);
    }
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(next));
    setDraft(next);
    setPartnerSaveTarget("local");
    setSaveStatus("saved");
    setRevealedMissingStepIds(new Set());
    setShowResumePrompt(false);
  };

  const startAdminFreshDraft = () => {
    const next = createInitialDraft();
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(next));
    setDraft(next);
    setPartnerSaveTarget("local");
    setSaveStatus("saved");
    setRevealedMissingStepIds(new Set());
    setShowResumePrompt(false);
    setInviteCreateState({ status: "idle" });
    setAdminIntakeImportSourceText("");
    setAdminIntakeImportProposal(null);
    setAdminIntakeImportError(null);
    setAdminIntakeImportNotice(null);
    window.history.pushState(null, "", "/");
    setPathname(window.location.pathname);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const createOrUpdateInviteUrl = async () => {
    if (!draft.data.category.categoryId || draft.submittedAt) return;
    setInviteCreateState({ status: "creating" });
    try {
      const response = await fetch("/api/admin/invites", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", accept: "application/json" },
        body: JSON.stringify({ draftId: draft.draftId, data: normalizeIntakeData(draft.data, activeFormConfig), files: draft.files }),
      });
      const body = (await response.json().catch(() => null)) as { invite?: { path: string }; error?: string } | null;
      if (!response.ok || !body?.invite?.path) throw new Error(body?.error ?? "초대 URL을 만들지 못했습니다.");
      const url = new URL(body.invite.path, window.location.origin).toString();
      let copied = false;
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch {
        copied = false;
      }
      setInviteCreateState({ status: "created", url, copied });
    } catch (error) {
      setInviteCreateState({ status: "error", message: error instanceof Error ? error.message : "초대 URL을 만들지 못했습니다." });
    }
  };

  const replaceLocalDraftWithInvite = () => {
    if (inviteBootstrap.status !== "conflict") return;
    applyLoadedInviteDraft(inviteBootstrap.draft);
  };

  const continueLocalDraftInstead = () => {
    setInviteBootstrap({ status: "idle" });
    if (!draft.data.category.categoryId) {
      window.history.replaceState(null, "", "/");
      setPathname("/");
      setShowResumePrompt(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    resumeStoredDraft();
  };

  const submit = async () => {
    const normalizedData = normalizeIntakeData(draft.data, activeFormConfig);
    const missing = collectMissingRequiredItems({ ...draft, data: normalizedData }, activeFormConfig);
    setMissingFields(missing);
    setSubmitError(null);
    if (missing.length) {
      setSubmitError("필수 항목을 먼저 채워주세요.");
      return;
    }
    try {
      await runPartnerMutation(async () => {
        const mutationDraft = await preparePartnerMutation();
        const capability = mutationDraft.inviteId
          ? { inviteId: mutationDraft.inviteId, draftVersion: mutationDraft.draftVersion ?? 0 }
          : await ensurePartnerInviteForMutation(mutationDraft);
        if (!capability) throw new Error("제출을 위한 초대 링크를 만들지 못했습니다.");
        const draftForSubmit = mutationDraft.inviteId ? mutationDraft : { ...mutationDraft, inviteId: capability.inviteId, draftVersion: capability.draftVersion };
        // 충돌 병합이 초안을 갱신했을 수 있으므로 제출 payload는 병합된 최신 초안으로 다시 만든다.
        const submitData = normalizeIntakeData(draftForSubmit.data, activeFormConfig);
        const mergedMissing = collectMissingRequiredItems({ ...draftForSubmit, data: submitData }, activeFormConfig);
        if (mergedMissing.length) {
          setMissingFields(mergedMissing);
          throw new Error("다른 곳의 수정이 반영되면서 필수 항목이 비었습니다. 내용 확인 후 다시 제출해주세요.");
        }
        const effectiveSchema = getEffectiveIntakeSchema(submitData, activeFormConfig);
        const submission = await submitPartnerDraft(draftForSubmit, submitData, effectiveSchema);
        latestDraftRef.current = { ...latestDraftRef.current, inviteId: draftForSubmit.inviteId, draftVersion: draftForSubmit.draftVersion, data: submitData, submittedAt: submission.submittedAt, submissionId: submission.id };
        setDraft((current) => ({ ...current, inviteId: draftForSubmit.inviteId, draftVersion: draftForSubmit.draftVersion, data: submitData, submittedAt: submission.submittedAt, submissionId: submission.id }));
        goToStep(steps.length - 1);
      });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "제출에 실패했습니다.");
    }
  };

  const shouldShowMissingRequiredForField = (field: IntakeField, sectionRevealed: boolean) => shouldRevealMissingRequiredForField(field, draft, sectionRevealed);

  const applyContractOffer = (offeredValues: string[], offeredRate: string) => {
    setDraft((current) => {
      const contract = current.data.contract;
      const contractType = offeredValues.length && !offeredValues.includes(contract.contractType) ? offeredValues[0] : contract.contractType;
      const sanitizedRate = sanitizeUpsellRate(offeredRate);
      const currentProfile = getCategoryProfile(current.data.category.categoryId);
      const categoryRate = currentProfile ? sanitizeUpsellRate(getDefaultUpsellRateForContext({ categoryId: currentProfile.id, domain: currentProfile.domain }, activeFormConfig)) : "";
      const fallbackRate = categoryRate || FIXED_UPSELL_COMMISSION_RATE;
      return {
        ...current,
        data: {
          ...current.data,
          contract: {
            ...contract,
            contractType,
            offeredContractTypes: offeredValues.join("\n"),
            offeredUpsellCommissionRate: offeredRate,
            upsellCommissionRate: isUpsellContractType(contractType) ? (sanitizedRate || fallbackRate) : "",
          },
        },
      };
    });
  };

  const renderContractOfferPanel = () => {
    if (!selectedProfile) return null;
    const offerOptions = getContractOptionsForContext({ categoryId: selectedProfile.id, domain: selectedProfile.domain }, activeFormConfig).options;
    if (!offerOptions.length) return null;
    const offeredSelection = parseOfferedContractTypes(draft.data.contract.offeredContractTypes);
    const selectedValues = offeredSelection.length ? offeredSelection : offerOptions.map((option) => option.value);
    const rateText = draft.data.contract.offeredUpsellCommissionRate;
    const categoryDefaultRate = sanitizeUpsellRate(getDefaultUpsellRateForContext({ categoryId: selectedProfile.id, domain: selectedProfile.domain }, activeFormConfig));
    const displayRate = sanitizeUpsellRate(rateText) || categoryDefaultRate || FIXED_UPSELL_COMMISSION_RATE;
    const hasUpsellSelected = selectedValues.some((value) => isUpsellContractType(value));

    const toggleOfferValue = (value: string, checked: boolean) => {
      const nextValues = offerOptions.map((option) => option.value).filter((candidate) => (candidate === value ? checked : selectedValues.includes(candidate)));
      if (!nextValues.length) return;
      applyContractOffer(nextValues, rateText);
    };

    return (
      <div className="admin-invite-toolbar__offer">
        <strong>파트너에게 보여줄 계약 옵션</strong>
        <div className="admin-invite-offer-options">
          {offerOptions.map((option) => {
            const checked = selectedValues.includes(option.value);
            return (
              <label key={option.value}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={checked && selectedValues.length === 1}
                  onChange={(event) => toggleOfferValue(option.value, event.target.checked)}
                />
                <span>{option.label.split("업셀링 n%").join(`업셀링 ${displayRate}%`)}</span>
              </label>
            );
          })}
        </div>
        {hasUpsellSelected && (
          <label className="admin-invite-offer-rate">
            <span>업셀링 수수료율</span>
            <input
              type="number"
              min={1}
              max={100}
              step={0.5}
              value={rateText}
              placeholder={categoryDefaultRate || FIXED_UPSELL_COMMISSION_RATE}
              onChange={(event) => applyContractOffer(selectedValues, event.target.value)}
            />
            <em>%</em>
          </label>
        )}
        <small>체크한 옵션만 파트너 신청서에 보여요. 수수료율은 기본값으로 채워지며 파트너가 수정할 수 있어요.</small>
      </div>
    );
  };

  const runAdminIntakeImport = async () => {
    if (!adminUser || draft.submittedAt) return;
    const sourceText = adminIntakeImportSourceText.trim();
    const categoryId = draft.data.category.categoryId;
    if (!categoryId) {
      setAdminIntakeImportError("문서를 분석하기 전에 상품 유형을 먼저 선택해주세요.");
      return;
    }
    if (!sourceText) {
      setAdminIntakeImportError("정리할 문서 원문을 붙여넣어주세요.");
      return;
    }
    if (sourceText.length > 60_000) {
      setAdminIntakeImportError("문서는 최대 60,000자까지 분석할 수 있어요.");
      return;
    }

    const importContext = currentAdminIntakeImportContext;
    const requestId = ++adminIntakeImportRequestRef.current;
    const isCurrentRequest = () => {
      const latestDraft = latestDraftRef.current;
      const latestContext = createIntakeImportContext(latestDraft, getEffectiveIntakeSchema(latestDraft.data, activeFormConfig).schemaHash);
      return isCurrentIntakeImportRequest(requestId, adminIntakeImportRequestRef.current, importContext, latestContext);
    };
    const form = createSubmissionPresentationManifest(steps);
    const category = {
      id: categoryId,
      label: draft.data.category.label || selectedCategoryBreadcrumb || categoryId,
      domain: draft.data.category.domain,
    };
    setAdminIntakeImportLoading(true);
    setAdminIntakeImportError(null);
    setAdminIntakeImportNotice(null);
    setAdminIntakeImportProposal(null);
    try {
      const response = await fetch("/api/admin/ai/intake-import", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", accept: "application/json" },
        body: JSON.stringify({ sourceText, category, form }),
      });
      const body = (await response.json().catch(() => null)) as IntakeImportResult | { error?: string } | null;
      if (!response.ok || !body || !("patches" in body) || !Array.isArray(body.patches)) {
        throw new Error((body as { error?: string } | null)?.error ?? "문서를 정리하지 못했습니다.");
      }
      if (!isCurrentRequest()) return;
      setAdminIntakeImportProposal({ ...importContext, result: { summary: body.summary, patches: body.patches } });
      setAdminIntakeImportNotice(body.patches.length ? `${body.patches.length}개 항목을 검토할 수 있어요.` : "원문에서 현재 설문 항목으로 옮길 정보를 찾지 못했습니다.");
    } catch (error) {
      if (isCurrentRequest()) setAdminIntakeImportError(error instanceof Error ? error.message : "문서를 정리하지 못했습니다.");
    } finally {
      if (isCurrentRequest()) setAdminIntakeImportLoading(false);
    }
  };

  const applyAdminIntakeImport = (overwriteExisting: boolean) => {
    const proposal = adminIntakeImportProposal;
    const current = latestDraftRef.current;
    const currentContext = createIntakeImportContext(current, getEffectiveIntakeSchema(current.data, activeFormConfig).schemaHash);
    if (!proposal || !isSameIntakeImportContext(proposal, currentContext) || current.submittedAt) return;
    if (overwriteExisting && !window.confirm("현재 작성된 내용을 AI 제안으로 덮어쓸 수 있어요. 계속할까요?")) return;

    const baselineData = applyCategoryPresetForTaxonomy(createDefaultIntakeData(), current.data.category.categoryId);
    const result = applyIntakeImportPatches({
      data: current.data,
      baselineData,
      patches: proposal.result.patches,
      overwriteExisting,
    });
    if (result.applied.length === 0) {
      setAdminIntakeImportNotice(overwriteExisting ? "반영할 수 있는 항목이 없어요." : "기존에 작성된 값이 있어 반영하지 않았어요. 필요하면 덮어쓰기를 선택해주세요.");
      return;
    }

    const nextData = normalizeIntakeData(result.data, activeFormConfig);
    setDraft((latest) => {
      const latestContext = createIntakeImportContext(latest, getEffectiveIntakeSchema(latest.data, activeFormConfig).schemaHash);
      return isSameIntakeImportContext(proposal, latestContext) && !latest.submittedAt ? { ...latest, data: nextData } : latest;
    });
    setAdminIntakeImportProposal(null);
    setAdminIntakeImportNotice(`${result.applied.length}개 항목을 ${overwriteExisting ? "덮어써서" : "빈 칸과 기본값에만"} 반영했어요.${result.skipped.length ? ` ${result.skipped.length}개 항목은 반영하지 않았어요.` : ""}`);
  };

  const renderAdminIntakeImport = () => {
    const categorySelected = Boolean(draft.data.category.categoryId);
    const activeProposal = adminIntakeImportProposal && isSameIntakeImportContext(adminIntakeImportProposal, currentAdminIntakeImportContext) ? adminIntakeImportProposal.result : null;
    const canAnalyze = categorySelected && adminIntakeImportSourceText.trim().length > 0 && !draft.submittedAt;
    return (
      <details className="admin-intake-import">
        <summary>AI 문서 일괄 입력</summary>
        <div className="admin-intake-import__body">
          <p>붙여넣은 문서에서 현재 설문에 맞는 정보를 찾아 제안합니다. 문서는 분석 요청을 위해 AI 서비스로 전송되며, 신청서에는 저장되지 않습니다. 개인정보 또는 전화번호가 포함된 문서는 분석하지 않으니 제거 후 붙여넣어주세요.</p>
          <label className="admin-intake-import__field">
            <span>문서 원문</span>
            <textarea
              value={adminIntakeImportSourceText}
              rows={9}
              maxLength={60_000}
              placeholder="업체 소개서, 카카오톡 대화, 메모, 기존 상품 설명 등 긁을 수 있는 정보를 모두 붙여넣어주세요. 비밀번호·토큰 등 민감한 정보는 제외해주세요."
              onChange={(event) => {
                setAdminIntakeImportSourceText(event.target.value);
                setAdminIntakeImportProposal(null);
                setAdminIntakeImportError(null);
                setAdminIntakeImportNotice(null);
              }}
              disabled={adminIntakeImportLoading || Boolean(draft.submittedAt)}
            />
          </label>
          <div className="admin-intake-import__controls">
            <small>{adminIntakeImportSourceText.length.toLocaleString()} / 60,000자 · {categorySelected ? "현재 상품 유형의 보이는 문항만 분석합니다." : "상품 유형을 먼저 선택해주세요."}</small>
            <button className="button secondary compact" type="button" onClick={runAdminIntakeImport} disabled={!canAnalyze || adminIntakeImportLoading}>
              {adminIntakeImportLoading ? "AI가 정리하는 중" : "문서 분석하기"}
            </button>
          </div>
          {adminIntakeImportError && <p className="admin-intake-import__error" role="alert">{adminIntakeImportError}</p>}
          {adminIntakeImportNotice && <p className="admin-intake-import__notice" aria-live="polite">{adminIntakeImportNotice}</p>}
          {activeProposal && (
            <section className="admin-intake-import__proposal" aria-label="AI 입력 제안" aria-live="polite">
              <strong>{activeProposal.summary || "AI 입력 제안"}</strong>
              <p>{activeProposal.patches.length}개 항목을 확인한 뒤 반영해주세요.</p>
              <ul>
                {activeProposal.patches.map((patch, index) => (
                  <li key={`${patch.path}-${index}`}>
                    <strong>{describeIntakeImportPatch(patch, steps)}</strong>
                    <span>{patch.value}</span>
                    {patch.reason && <small>{patch.reason}</small>}
                  </li>
                ))}
              </ul>
              <div className="button-row">
                <button className="button secondary compact" type="button" onClick={() => applyAdminIntakeImport(false)} disabled={adminIntakeImportLoading}>빈 칸과 기본값에만 반영</button>
                <button className="button danger compact" type="button" onClick={() => applyAdminIntakeImport(true)} disabled={adminIntakeImportLoading}>기존 내용도 덮어쓰기</button>
              </div>
            </section>
          )}
        </div>
      </details>
    );
  };

  const renderAdminToolbar = (variant: "floating" | "inline" = "floating") => {
    const isAdminPrefillPreview = new URLSearchParams(window.location.search).get("adminPrefill") === "1";
    if (!adminUser || !isAdminPrefillPreview) return null;
    const toolbarClassName = variant === "inline" ? "admin-invite-toolbar admin-invite-toolbar--inline" : "admin-invite-toolbar";
    const canCreateInvite = Boolean(draft.data.category.categoryId) && !draft.submittedAt;
    const helperText = !draft.data.category.categoryId
      ? "상품 유형을 선택하면 초대 URL을 만들 수 있어요."
      : draft.submittedAt
        ? "이미 제출된 신청서는 새 초대 URL을 만들 수 없어요."
        : inviteCreateState.status === "created"
          ? inviteCreateState.copied ? "URL을 클립보드에 복사했어요." : "아래 URL을 복사해 파트너에게 보내주세요."
          : "현재 작성 내용을 파트너가 이어서 작성할 수 있는 링크로 저장합니다.";
    return (
      <aside className={toolbarClassName} aria-label="어드민 초대 링크 도구">
        <div className="admin-invite-toolbar__meta">
          <strong>어드민 프리필 모드</strong>
          <span>{adminUser.name || adminUser.email}</span>
          <small>{helperText}</small>
          {inviteCreateState.status === "created" && <code>{inviteCreateState.url}</code>}
          {inviteCreateState.status === "error" && <small className="admin-invite-toolbar__error">{inviteCreateState.message}</small>}
          {shouldShowAdminIntakeImport(pathname, isCategoryFlow) && renderAdminIntakeImport()}
          {canCreateInvite && renderContractOfferPanel()}
        </div>
        <div className="admin-invite-toolbar__actions">
          <button className="button primary compact" type="button" onClick={createOrUpdateInviteUrl} disabled={!canCreateInvite || inviteCreateState.status === "creating"}>
            {inviteCreateState.status === "creating" ? "URL 저장 중" : inviteCreateState.status === "created" ? "초대 URL 갱신" : "초대 URL 만들기"}
          </button>
          <button className="button secondary compact" type="button" onClick={startAdminFreshDraft}>새 신청서 시작</button>
        </div>
      </aside>
    );
  };

  const renderStepField = (sectionStep: IntakeStep, field: IntakeField) => {
    const sectionMissingRevealed = revealedMissingStepIds.has(sectionStep.id);
    if (sectionStep.id === "company" && field.id === "storeNameKo") {
      const storeNameKo = draft.data.company.storeNameKo.trim();
      return (
        <Fragment key={field.id}>
          <FieldControl field={field} value={getPathValue(draft.data, field.path)} onChange={updateField} showMissingRequired={shouldShowMissingRequiredForField(field, sectionMissingRevealed)} />
          <div className="kakao-lookup-cta">
            <div>
              <strong>상점명 입력 후 카카오맵 정보를 불러올 수 있어요.</strong>
              <p>주소, 대표 전화번호, 카카오맵 링크, 분류를 한 번에 채웁니다.</p>
            </div>
            <button className="button secondary" type="button" onClick={openKakaoPlaceLookup} disabled={storeNameKo.length < 2}>
              카카오맵에서 찾아보기
            </button>
          </div>
        </Fragment>
      );
    }
    if (sectionStep.id === "company" && field.id === "useContactInfoForTaxInvoice") {
      return (
        <TaxInvoiceContactField
          key={field.id}
          checkboxField={field}
          checkboxValue={getPathValue(draft.data, field.path)}
          contactFields={sectionStep.fields?.filter((candidate) => TAX_INVOICE_CONTACT_FIELD_IDS.includes(candidate.id)) ?? []}
          values={draft.data}
          onChange={updateField}
          showMissingRequired={sectionMissingRevealed}
        />
      );
    }
    if (sectionStep.id === "company" && ["useContactEmailForNotifications", "notificationEmails", "useContactPhoneForAlimtalk", "notificationPhones", ...TAX_INVOICE_CONTACT_FIELD_IDS].includes(field.id)) {
      return null;
    }
    if (sectionStep.id === "company" && field.id === "contactEmail") {
      return (
        <ContactNotificationField
          key={field.id}
          primaryField={field}
          primaryValue={getPathValue(draft.data, field.path)}
          checkboxField={sectionStep.fields?.find((candidate) => candidate.id === "useContactEmailForNotifications")}
          checkboxValue={getPathValue(draft.data, "reservation.useContactEmailForNotifications")}
          separateField={sectionStep.fields?.find((candidate) => candidate.id === "notificationEmails")}
          separateValue={getPathValue(draft.data, "reservation.notificationEmails")}
          onChange={updateField}
          showMissingRequired={shouldShowMissingRequiredForField(field, sectionMissingRevealed)}
        />
      );
    }
    if (sectionStep.id === "company" && field.id === "contactPhone") {
      return (
        <ContactNotificationField
          key={field.id}
          primaryField={field}
          primaryValue={getPathValue(draft.data, field.path)}
          checkboxField={sectionStep.fields?.find((candidate) => candidate.id === "useContactPhoneForAlimtalk")}
          checkboxValue={getPathValue(draft.data, "reservation.useContactPhoneForAlimtalk")}
          separateField={sectionStep.fields?.find((candidate) => candidate.id === "notificationPhones")}
          separateValue={getPathValue(draft.data, "reservation.notificationPhones")}
          onChange={updateField}
          showMissingRequired={shouldShowMissingRequiredForField(field, sectionMissingRevealed)}
        />
      );
    }
    if (sectionStep.id === "content" && field.id === "catchphrase") {
      return (
        <CatchphrasePreviewField
          key={field.id}
          field={field}
          value={getPathValue(draft.data, field.path)}
          storeNameKo={draft.data.company.storeNameKo}
          onChange={updateField}
          showMissingRequired={shouldShowMissingRequiredForField(field, sectionMissingRevealed)}
        />
      );
    }
    if (sectionStep.id === "content" && field.id === "availableLanguages") {
      return (
        <LanguageSupportField
          key={field.id}
          field={field}
          value={getPathValue(draft.data, field.path)}
          onChange={updateField}
          showMissingRequired={shouldShowMissingRequiredForField(field, sectionMissingRevealed)}
        />
      );
    }
    if (sectionStep.id === "content" && field.id === "googleReviewIntegration") {
      const value = getPathValue(draft.data, field.path);
      return (
        <div className="field-with-nudge" key={field.id}>
          <FieldControl field={field} value={value} onChange={updateField} showMissingRequired={shouldShowMissingRequiredForField(field, sectionMissingRevealed)} />
          {fieldValueToString(value) === "no" && (
            <aside className="integration-nudge" role="note">
              <strong>가능하면 연동을 추천합니다.</strong>
              <p>추가 비용 없이 제공되며, 구글맵 리뷰를 예약페이지에 함께 보여줘 외국인 고객의 신뢰 형성과 예약 전환에 도움이 됩니다. 다수 제휴 업체가 활용 중입니다.</p>
            </aside>
          )}
        </div>
      );
    }
    if (sectionStep.id === "content" && field.id === "googleBookingIntegration") {
      return (
        <GoogleBookingIntegrationField
          key={field.id}
          field={field}
          optOutPromptEnabled={sectionStep.guards?.some((guard) => guard.path === field.path) ?? false}
          value={getPathValue(draft.data, field.path)}
          onChange={updateField}
          showMissingRequired={shouldShowMissingRequiredForField(field, sectionMissingRevealed)}
        />
      );
    }
    if (sectionStep.id === "content" && field.id === "creatripExclusiveBenefit") {
      return (
        <ExclusiveBenefitField
          key={field.id}
          field={field}
          value={getPathValue(draft.data, field.path)}
          onChange={updateField}
          showMissingRequired={shouldShowMissingRequiredForField(field, sectionMissingRevealed)}
        />
      );
    }
    if (field.id === "adminPassword") {
      return <FieldControl key={field.id} field={{ ...field, helperText: getAdminPasswordHelperText(draft, field.helperText) }} value={getPathValue(draft.data, field.path)} onChange={updateField} showMissingRequired={shouldShowMissingRequiredForField(field, sectionMissingRevealed)} />;
    }
    return <FieldControl key={field.id} field={field} value={getPathValue(draft.data, field.path)} onChange={updateField} showMissingRequired={shouldShowMissingRequiredForField(field, sectionMissingRevealed)} />;
  };

  const runAiFill = async (action: AiFillAction, context: { repeaterId?: string; itemIndex?: number } | undefined, noticeStepId: string) => {
    const loadingKey = `${action}:${context?.repeaterId ?? "step"}:${context?.itemIndex ?? ""}`;
    setAiNotice(null);
    setSubmitError(null);
    if (!draft.data.company.storeNameKo.trim()) {
      setSubmitError("AI로 채우기 전에 상점명 / 병원명(한국어)을 먼저 입력해주세요.");
      return;
    }
    setAiLoadingKey(loadingKey);
    try {
      const response = await fetch("/api/ai/fill", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, data: draft.data, context }),
      });
      const body = (await response.json().catch(() => null)) as AiFillResponse | { error?: string } | null;
      if (!response.ok || !body || !("patches" in body)) throw new Error((body as { error?: string } | null)?.error ?? "AI로 채우지 못했습니다.");

      let appliedCount = 0;
      setDraft((current) => {
        let nextData = current.data;
        for (const patch of body.patches) {
          const currentValue = getPathValue(nextData, patch.path);
          if (fieldValueToString(currentValue).trim()) continue;
          nextData = setPathValue(nextData, patch.path, patch.value);
          appliedCount += 1;
        }
        return appliedCount > 0 ? { ...current, data: nextData } : current;
      });
      setAiNotice({ message: appliedCount > 0 ? `${appliedCount}개 항목을 채웠어요.` : "이미 작성된 항목은 그대로 두었어요.", stepId: noticeStepId });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "AI로 채우지 못했습니다.");
    } finally {
      setAiLoadingKey(null);
    }
  };

  const searchKakaoPlaces = async (queryOverride?: string) => {
    const query = (queryOverride ?? kakaoLookup.query).trim();
    if (query.length < 2) {
      setKakaoLookup((current) => ({ ...current, error: "두 글자 이상 입력해주세요.", items: [], searchedQuery: null }));
      return;
    }

    setKakaoLookup((current) => ({ ...current, query, loading: true, error: null, searchedQuery: query }));
    try {
      const params = new URLSearchParams({ query, size: "8" });
      const response = await fetch(`/api/kakao-map/places?${params.toString()}`);
      const body = (await response.json().catch(() => null)) as { items?: KakaoPlace[]; error?: string; message?: string } | null;
      if (!response.ok || !body?.items) throw new Error(body?.message ?? body?.error ?? "카카오맵에서 장소를 찾지 못했습니다.");
      setKakaoLookup((current) => ({ ...current, loading: false, items: body.items ?? [], error: null }));
    } catch (error) {
      setKakaoLookup((current) => ({ ...current, loading: false, items: [], error: error instanceof Error ? error.message : "카카오맵 조회에 실패했습니다." }));
    }
  };

  const openKakaoPlaceLookup = () => {
    const query = draft.data.company.storeNameKo.trim();
    setSubmitError(null);
    setKakaoLookup({ ...initialKakaoLookupState, open: true, query });
    if (query.length >= 2) void searchKakaoPlaces(query);
  };

  const closeKakaoPlaceLookup = () => {
    setKakaoLookup((current) => ({ ...current, open: false }));
  };

  const applyKakaoPlace = (place: KakaoPlace) => {
    const patches = createKakaoPlacePatches(place);
    setDraft((current) => {
      let nextData = current.data;
      for (const patch of patches) {
        nextData = setPathValue(nextData, patch.path, patch.value);
      }
      return { ...current, data: nextData };
    });
    setAiNotice({ message: `${patches.length}개 항목을 카카오맵 기준으로 채웠어요.`, stepId: "company" });
    closeKakaoPlaceLookup();
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName;
      const isTyping = target?.isContentEditable || tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT";
      if (isTyping) return;
      if (event.key === "ArrowRight") {
        event.preventDefault();
        goNextPartWithCheck();
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goToPart(currentPartIndex - 1);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [draft]);

  if (categoryTaxonomyStatus !== "ready") {
    return <CategoryTaxonomyLoadScreen status={categoryTaxonomyStatus} />;
  }

  const inviteUnavailable = inviteBootstrap.status === "expired" || inviteBootstrap.status === "notFound" || inviteBootstrap.status === "submitted" || inviteBootstrap.status === "error";
  if (inviteId && (draft.inviteId !== inviteId || inviteUnavailable)) {
    if (inviteBootstrap.status === "conflict") {
      return (
        <main className="resume-screen">
          <section className="resume-card">
            <p className="eyebrow">크리에이트립 입점을 위한 상품요청서</p>
            <h1>초대받은 신청서로 이어서 작성할까요?</h1>
            <p>이 브라우저에 다른 신청서 작성 내용이 남아 있어요. 기존 내용을 유지하거나, 초대받은 신청서로 교체할 수 있습니다.</p>
            <div className="resume-assurance">
              <strong>초대 신청서</strong>
              <span>{inviteBootstrap.draft.data.company.storeNameKo || inviteBootstrap.draft.data.category.label || inviteBootstrap.draft.draftId}</span>
            </div>
            <div className="button-row">
              <button className="button primary" type="button" onClick={replaceLocalDraftWithInvite}>초대 신청서로 교체하기</button>
              <button className="button secondary" type="button" onClick={continueLocalDraftInstead}>기존 신청서 계속 작성</button>
            </div>
          </section>
        </main>
      );
    }

    if (inviteBootstrap.status === "expired" || inviteBootstrap.status === "notFound" || inviteBootstrap.status === "submitted" || inviteBootstrap.status === "error") {
      const title = inviteBootstrap.status === "expired"
        ? "초대 링크가 만료되었어요"
        : inviteBootstrap.status === "notFound"
          ? "초대 링크를 찾을 수 없어요"
          : inviteBootstrap.status === "submitted"
            ? "이미 제출된 신청서예요"
            : "초대 정보를 불러오지 못했어요";
      const description = inviteBootstrap.status === "submitted" ? "이 초대 링크로 작성한 신청서는 이미 제출되었습니다. 수정이 필요하면 크리에이트립 담당자에게 문의해주세요." : inviteBootstrap.status === "error" ? inviteBootstrap.message : "링크를 다시 확인하거나 크리에이트립 담당자에게 새 링크를 요청해주세요.";
      return (
        <main className="resume-screen">
          <section className="resume-card">
            <p className="eyebrow">크리에이트립 입점을 위한 상품요청서</p>
            <h1>{title}</h1>
            <p>{description}</p>
            <div className="button-row">
              <button className="button primary" type="button" onClick={() => { window.history.replaceState(null, "", "/"); setPathname("/"); }}>처음부터 직접 작성하기</button>
            </div>
          </section>
        </main>
      );
    }

    return (
      <main className="resume-screen">
        <section className="resume-card">
          <p className="eyebrow">크리에이트립 입점을 위한 상품요청서</p>
          <h1>초대받은 신청서를 불러오는 중이에요</h1>
          <p>담당자가 미리 작성한 내용을 확인하고 있습니다.</p>
        </section>
      </main>
    );
  }

  if (showResumePrompt) {
    return (
      <main className="resume-screen">
        <section className="resume-card">
          <p className="eyebrow">크리에이트립 입점을 위한 상품요청서</p>
          <h1>작성 중이던 입점 정보가 있어요</h1>
          <p>마지막 작성: {formatDateTime(draft.updatedAt)}</p>
          <div className="resume-assurance">
            <strong>작성하던 내용이 남아 있어요.</strong>
            <span>이어서 진행하거나 새로 시작할 수 있습니다.</span>
          </div>
          <div className="button-row">
            <button className="button primary" onClick={resumeStoredDraft}>이어서 작성하기</button>
            <button className="button secondary" onClick={startNewDraft}>새로 작성하기</button>
          </div>
        </section>
      </main>
    );
  }

  if (!isCategoryFlow) {
    return (
      <>
        <CategoryLanding selectedCategoryId={draft.data.category.categoryId} onSelect={navigateToCategory} categoryGroups={visibleCategoryGroups} />
        {renderAdminToolbar()}
      </>
    );
  }

  const renderStepSection = (sectionStep: IntakeStep) => {
    const sectionFieldGroups = getStepFieldGroups(sectionStep.id, draft.data.contract.contractType, selectedProfile);
    const sectionMissingRevealed = revealedMissingStepIds.has(sectionStep.id);
    return (
      <section className="part-section" key={sectionStep.id} id={`section-${sectionStep.id}`}>
        <div className="step-heading">
          <h2>{sectionStep.title}</h2>
          <p>{sectionStep.description}</p>
        </div>

        {aiNotice?.stepId === sectionStep.id && <div className="ai-notice">{aiNotice.message}</div>}

        {sectionStep.tips?.length ? <TipCards tips={sectionStep.tips} /> : null}

        {sectionStep.fields?.length ? (
          <GroupedFieldControls
            fields={sectionStep.fields}
            groups={sectionFieldGroups}
            aiLoadingKey={aiLoadingKey}
            onAiFill={(action, context) => runAiFill(action, context, sectionStep.id)}
            renderField={(field) => renderStepField(sectionStep, field)}
          />
        ) : null}

        {sectionStep.repeaters?.map((repeater) => (
          repeater.id === "products" ? (
            <ProductSpreadsheetControl
              key={repeater.id}
              repeater={repeater}
              value={getPathValue(draft.data, repeater.path)}
              contractType={draft.data.contract.contractType}
              categoryProfile={selectedProfile}
              onAdd={addRepeaterItem}
              onRemove={removeRepeaterItem}
              onReplace={replaceRepeaterItems}
              files={draft.files}
              uploadStatus={uploadStatus}
              onUpload={uploadFiles}
              onRemoveFile={removeUploadedFile}
              onReorderFile={reorderUploadedFile}
              onRenameFile={renameUploadedFile}
              showMissingRequired={sectionMissingRevealed}
            />
          ) : (
            <RepeaterControl
              key={repeater.id}
              repeater={repeater}
              value={getPathValue(draft.data, repeater.path)}
              onAdd={addRepeaterItem}
              onRemove={removeRepeaterItem}
              onChange={updateRepeaterItem}
              aiLoadingKey={aiLoadingKey}
              onAiFill={(action, context) => runAiFill(action, context, sectionStep.id)}
              categoryProfile={selectedProfile}
              files={draft.files}
              uploadStatus={uploadStatus}
              onUpload={uploadFiles}
              onRemoveFile={removeUploadedFile}
              onReorderFile={reorderUploadedFile}
              onRenameFile={renameUploadedFile}
              showMissingRequired={sectionMissingRevealed}
            />
          )
        ))}

        {sectionStep.id === "photos" && <PhotoDisplayGuide />}

        {sectionStep.fileUploads?.length ? (
          <div className="upload-grid">
            {sectionStep.fileUploads.map((definition) => (
              <Fragment key={definition.id}>
                <FileUploadCard
                  definition={definition}
                  files={draft.files.filter((file) => isUploadedFileForDefinition(file, definition))}
                  status={uploadStatus[definition.id]}
                  onUpload={uploadFiles}
                  onRemove={removeUploadedFile}
                  onReorder={reorderUploadedFile}
                  onRename={renameUploadedFile}
                  showMissingRequired={sectionMissingRevealed}
                />
                {sectionStep.id === "photos" && definition.id === "listingPhotos" && <PhotoPlacementPreview files={draft.files} />}
              </Fragment>
            ))}
          </div>
        ) : null}

        {sectionStep.id === "review" && <ReviewPanel draft={draft} steps={steps} onGoToStep={goToStep} onGoToMissingField={goToMissingField} onGoToCategories={() => {
          window.history.pushState(null, "", "/");
          setPathname("/");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }} onSubmit={submit} submissionDisabled={uploadInProgress || Boolean(draft.submittedAt)} />}
      </section>
    );
  };

  return (
    <>
    <main className="survey-shell">
      <header className="survey-top">
        <div className="survey-brand">
          <small>크리에이트립 입점을 위한</small>
          <strong>상품요청서</strong>
        </div>
        <div className="survey-save" aria-live="polite">
          <span className={`survey-save--${saveStatusMessage.tone}`} key={saveStatusMessage.tone === "success" ? draft.updatedAt : saveStatusMessage.tone}>
            {isStepTransitionSaving ? "변경 내용을 저장하는 중" : saveStatusMessage.message}
          </span>
        </div>
      </header>

      <div className="survey-progress" aria-label={`진행률 ${progress}%`}>
        <span style={{ width: `${progress}%` }} />
      </div>

      <section className="survey-card">
        <nav className="part-tabs" aria-label="작성 파트">
          {INTAKE_PARTS.map((part, index) => {
            const completion = partCompletions[index];
            const isActive = index === currentPartIndex;
            const isComplete = completion.total > 0 && completion.done >= completion.total;
            return (
              <button key={part.id} type="button" className={`part-tab${isActive ? " is-active" : ""}${isComplete ? " is-complete" : ""}`} onClick={() => goToPart(index)} aria-current={isActive ? "step" : undefined}>
                <em>{isComplete ? "✓" : part.label}</em>
                <span>
                  <strong>{part.title}</strong>
                  <small>{completion.total > 0 ? `필수 ${completion.done}/${completion.total}` : "확인만 하면 돼요"}</small>
                </span>
              </button>
            );
          })}
        </nav>

        <div className="survey-meta">
          <span>입력 내용은 자동 저장돼요 · 언제든 이어서 작성할 수 있어요</span>
          <span>첨부 파일 {uploadedCount}개</span>
          {draft.inviteId && <small>참여자마다 보고 있는 화면은 다를 수 있어요.</small>}
        </div>

        {draft.revisionReason && (
          <div className="resume-assurance" role="note">
            <strong>수정 요청 사유</strong>
            <span>{draft.revisionReason}</span>
          </div>
        )}

        {coeditNotice && (
          <div className={`coedit-notice coedit-notice--${coeditNotice.kind}`} role="status">
            <strong>{coeditNotice.kind === "conflict" ? "같은 항목을 동시에 수정했어요" : "다른 참여자의 변경을 반영했어요"}</strong>
            <span>{coeditNotice.message}</span>
          </div>
        )}

        <div className="step-heading part-heading">
          <p className="eyebrow">파트 {currentPartIndex + 1} / {INTAKE_PARTS.length}</p>
          <h1>{currentPart.title}</h1>
          <p>{currentPart.description}</p>
        </div>

        {stepNotice && (
          <div className="alert warning" role="status">
            <strong>덜 작성된 항목이 있어요</strong>
            <p>{stepNotice}</p>
          </div>
        )}

        {submitError && (
          <div className="alert error" role="alert">
            <strong>{submitError}</strong>
            {missingFields.length > 0 && (
              <>
                <p>누락: {missingRequiredLabels(missingFields).slice(0, 8).join(", ")}{missingFields.length > 8 ? ` 외 ${missingFields.length - 8}개` : ""}</p>
                <button className="button secondary compact missing-jump-button" type="button" onClick={() => goToMissingField(missingFields[0])}>
                  첫 누락 항목으로 이동
                </button>
              </>
            )}
          </div>
        )}

        {draft.submittedAt && currentPartIndex === INTAKE_PARTS.length - 1 && (
          <div className="alert success">
            <strong>제출이 완료되었습니다.</strong>
            <p>제출 ID: {draft.submissionId} · 크리에이트립 담당자가 내용을 확인한 뒤 연락드릴게요.</p>
          </div>
        )}

        {partSteps.map((sectionStep) => renderStepSection(sectionStep))}

        {renderAdminToolbar("inline")}

        <footer className="survey-navigation">
          <button className="button ghost" type="button" onClick={() => goToPart(currentPartIndex - 1)} disabled={currentPartIndex <= 0}>
            이전 파트
          </button>
          <span className={`survey-footer-save survey-save--${saveStatusMessage.tone}`} aria-hidden="true">{saveStatusMessage.message}</span>
          {currentPartIndex < INTAKE_PARTS.length - 1 ? (
            <button className="button primary" type="button" onClick={goNextPartWithCheck}>
              다음 파트
            </button>
          ) : (
            <button className="button primary" type="button" onClick={submit} disabled={uploadInProgress || Boolean(draft.submittedAt)}>
              {draft.submittedAt ? "제출 완료" : uploadInProgress ? "업로드 중" : "제출하기"}
            </button>
          )}
        </footer>
      </section>

      {kakaoLookup.open && (
        <KakaoPlaceLookupModal
          state={kakaoLookup}
          onClose={closeKakaoPlaceLookup}
          onQueryChange={(query) => setKakaoLookup((current) => ({ ...current, query }))}
          onSearch={searchKakaoPlaces}
          onApply={applyKakaoPlace}
        />
      )}
    </main>
    </>
  );
}

function GroupedFieldControls({
  fields,
  groups,
  aiLoadingKey,
  aiContext,
  onAiFill,
  renderField,
}: {
  fields: IntakeField[];
  groups?: FieldGroupDefinition[];
  aiLoadingKey?: string | null;
  aiContext?: { repeaterId?: string; itemIndex?: number };
  onAiFill?: (action: AiFillAction, context?: { repeaterId?: string; itemIndex?: number }) => void;
  renderField: (field: IntakeField) => ReactNode;
}) {
  if (!groups?.length) {
    return <div className="form-grid">{fields.map((field) => renderField(field))}</div>;
  }

  const fieldsById = new Map(fields.map((field) => [field.id, field]));
  const usedFieldIds = new Set<string>();
  const visibleGroups = groups
    .map((group) => {
      const groupFields = group.fieldIds.map((fieldId) => fieldsById.get(fieldId)).filter(Boolean) as IntakeField[];
      groupFields.forEach((field) => usedFieldIds.add(field.id));
      return { ...group, fields: groupFields };
    })
    .filter((group) => group.fields.length > 0);

  const ungroupedFields = fields.filter((field) => !usedFieldIds.has(field.id));
  if (ungroupedFields.length > 0) {
    visibleGroups.push({ title: "기타", fieldIds: ungroupedFields.map((field) => field.id), fields: ungroupedFields });
  }

  return (
    <div className="field-groups">
      {visibleGroups.map((group, index) => {
        const requiredCount = group.fields.filter((field) => field.required && !field.readOnly).length;
        const aiKey = group.aiAction ? `${group.aiAction}:${aiContext?.repeaterId ?? "step"}:${aiContext?.itemIndex ?? ""}` : null;
        const isAiLoading = Boolean(aiKey && aiLoadingKey === aiKey);
        const shouldOpenGroup = (group.defaultOpen ?? requiredCount > 0) || index === 0;
        return (
          <details className="field-group" key={group.title} open={shouldOpenGroup}>
            <summary>
              <span>
                <strong>{group.title}</strong>
                {group.description && <small>{group.description}</small>}
              </span>
              <div className="group-summary-actions">
                <em>{requiredCount ? `필수 ${requiredCount}` : `${group.fields.length}개`}</em>
                {group.aiAction && onAiFill && (
                  <div className="ai-fill-action">
                    <button
                      className="ai-fill-button"
                      type="button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onAiFill(group.aiAction!, aiContext);
                      }}
                      disabled={isAiLoading}
                    >
                      {isAiLoading ? "채우는 중" : "AI로 채우기"}
                    </button>
                    {isAiLoading && (
                      <small className="ai-fill-warning" aria-live="polite">
                        AI는 잘못된 정보를 표기할 수 있으니 작성된 내용을 꼭 확인해주세요.
                      </small>
                    )}
                  </div>
                )}
              </div>
            </summary>
            {group.detail && <div className="group-detail">{group.detail}</div>}
            <div className="form-grid grouped">{group.fields.map((field) => renderField(field))}</div>
          </details>
        );
      })}
    </div>
  );
}

function TipCards({ tips }: { tips: NonNullable<IntakeStep["tips"]> }) {
  return (
    <div className="tip-grid">
      {tips.map((tip) => (
        <article key={tip.title} className={`tip-card ${tip.kind ?? "info"}`}>
          <strong>{tip.title}</strong>
          <p>{tip.body}</p>
        </article>
      ))}
    </div>
  );
}

function KakaoPlaceLookupModal({
  state,
  onClose,
  onQueryChange,
  onSearch,
  onApply,
}: {
  state: KakaoPlaceLookupState;
  onClose: () => void;
  onQueryChange: (query: string) => void;
  onSearch: (query?: string) => void | Promise<void>;
  onApply: (place: KakaoPlace) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void onSearch();
  };

  return (
    <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="lookup-modal" role="dialog" aria-modal="true" aria-labelledby="kakao-lookup-title">
        <div className="lookup-modal-head">
          <div>
            <p className="eyebrow">Kakao Map lookup</p>
            <h3 id="kakao-lookup-title">카카오맵 장소 찾기</h3>
            <p>상점명이나 병원명을 검색하고, 맞는 장소를 선택하면 입력 가능한 위치 정보를 자동으로 채웁니다.</p>
          </div>
          <button className="modal-close" type="button" aria-label="카카오맵 검색 닫기" onClick={onClose}>×</button>
        </div>

        <form className="lookup-search" onSubmit={submitSearch}>
          <label htmlFor="kakao-place-query">검색어</label>
          <div>
            <input
              ref={inputRef}
              id="kakao-place-query"
              type="search"
              value={state.query}
              placeholder="예: 홍대 00의원"
              onChange={(event) => onQueryChange(event.target.value)}
            />
            <button className="button primary" type="submit" disabled={state.loading}>{state.loading ? "검색 중" : "검색"}</button>
          </div>
          <small>카카오맵 장소 검색으로 기본 정보를 불러옵니다.</small>
        </form>

        {state.error && <div className="alert error lookup-alert" role="alert"><strong>{state.error}</strong></div>}

        <div className="lookup-results" aria-live="polite">
          {state.loading ? (
            <p className="lookup-empty">카카오맵에서 장소를 찾는 중입니다.</p>
          ) : state.items.length > 0 ? (
            <ul>
              {state.items.map((place) => {
                const patches = createKakaoPlacePatches(place);
                return (
                  <li key={place.id}>
                    <article className="lookup-result-card">
                      <div>
                        <strong>{place.placeName}</strong>
                        <p>{formatKakaoPlaceAddress(place)}</p>
                        <span>{[place.categoryName, place.phone].filter(Boolean).join(" · ") || "추가 정보 없음"}</span>
                        {place.placeUrl && <a href={place.placeUrl} target="_blank" rel="noreferrer">카카오맵에서 보기</a>}
                      </div>
                      <div className="lookup-fill-preview">
                        <small>채워질 항목</small>
                        <p>{patches.map((patch) => patch.label).join(", ")}</p>
                        <button className="button secondary" type="button" onClick={() => onApply(place)}>이 정보로 채우기</button>
                      </div>
                    </article>
                  </li>
                );
              })}
            </ul>
          ) : state.searchedQuery ? (
            <p className="lookup-empty">“{state.searchedQuery}” 검색 결과가 없습니다. 지점명이나 지역명을 함께 입력해보세요.</p>
          ) : (
            <p className="lookup-empty">상점명 또는 병원명을 입력하면 후보 장소가 여기에 표시됩니다.</p>
          )}
        </div>
      </section>
    </div>
  );
}

function ContactNotificationField({
  primaryField,
  primaryValue,
  checkboxField,
  checkboxValue,
  separateField,
  separateValue,
  onChange,
  showMissingRequired = false,
}: {
  primaryField: IntakeField;
  primaryValue: unknown;
  checkboxField?: IntakeField;
  checkboxValue: unknown;
  separateField?: IntakeField;
  separateValue: unknown;
  onChange: (field: IntakeField, value: string | boolean) => void;
  showMissingRequired?: boolean;
}) {
  return (
    <div className="contact-notification-field">
      <FieldControl field={primaryField} value={primaryValue} onChange={onChange} showMissingRequired={showMissingRequired} />
      {checkboxField && (
        <label className="inline-notification-toggle">
          <input type="checkbox" checked={checkboxValue === true} onChange={(event) => onChange(checkboxField, event.target.checked)} />
          <span>{checkboxField.checkboxText ?? "예약 알림을 이 정보로 받을게요"}</span>
        </label>
      )}
      {checkboxValue !== true && separateField && (
        <div className="separate-notification-input">
          <FieldControl field={separateField} value={separateValue} onChange={onChange} showMissingRequired={showMissingRequired} />
        </div>
      )}
    </div>
  );
}

function TaxInvoiceContactField({
  checkboxField,
  checkboxValue,
  contactFields,
  values,
  onChange,
  showMissingRequired = false,
}: {
  checkboxField: IntakeField;
  checkboxValue: unknown;
  contactFields: IntakeField[];
  values: unknown;
  onChange: (field: IntakeField, value: string | boolean) => void;
  showMissingRequired?: boolean;
}) {
  const helperTextId = checkboxField.helperText ? `${checkboxField.id}-help` : undefined;

  return (
    <section className="tax-invoice-contact-field" aria-labelledby={`${checkboxField.id}-label`}>
      <div className="tax-invoice-contact-heading">
        <strong id={`${checkboxField.id}-label`}>{checkboxField.label}</strong>
        {checkboxField.helperText && helperTextId && <small id={helperTextId}>{checkboxField.helperText}</small>}
      </div>
      <label className="inline-notification-toggle" htmlFor={checkboxField.id}>
        <input
          id={checkboxField.id}
          name={checkboxField.id}
          type="checkbox"
          checked={checkboxValue === true}
          aria-describedby={helperTextId}
          onChange={(event) => onChange(checkboxField, event.target.checked)}
        />
        <span>{checkboxField.checkboxText}</span>
      </label>
      {checkboxValue !== true && (
        <div className="separate-tax-invoice-inputs">
          {contactFields.map((field) => (
            <FieldControl
              key={field.id}
              field={field}
              value={getPathValue(values, field.path)}
              onChange={onChange}
              showMissingRequired={showMissingRequired}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function CatchphrasePreviewField({
  field,
  value,
  storeNameKo,
  onChange,
  showMissingRequired = false,
}: {
  field: IntakeField;
  value: unknown;
  storeNameKo: string;
  onChange: (field: IntakeField, value: string | boolean) => void;
  showMissingRequired?: boolean;
}) {
  const storeName = storeNameKo.trim() || "우리치과병원";
  const catchphrase = fieldValueToString(value).trim() || "캐치프레이즈";

  return (
    <div className="field-preview-pair">
      <FieldControl field={field} value={value} onChange={onChange} showMissingRequired={showMissingRequired} />
      <aside className="title-preview-card" aria-live="polite">
        <span>상품 제목 미리보기</span>
        <strong>{storeName} | {catchphrase}</strong>
        <p>상점명은 업체 기본 정보의 한국어 상점명을 사용합니다.</p>
      </aside>
    </div>
  );
}

const GOOGLE_BOOKING_SIMULATION = {
  visitors: 1000,
  baselineRate: 0.02,
  creatripRate: 0.056,
};

function formatPercent(rate: number): string {
  return `${(rate * 100).toFixed(1).replace(/\.0$/, "")}%`;
}

export function GoogleBookingIntegrationField({
  field,
  optOutPromptEnabled,
  value,
  onChange,
  showMissingRequired = false,
}: {
  field: IntakeField;
  optOutPromptEnabled: boolean;
  value: unknown;
  onChange: (field: IntakeField, value: string | boolean) => void;
  showMissingRequired?: boolean;
}) {
  const [optOutPromptOpen, setOptOutPromptOpen] = useState(false);
  const currentValue = fieldValueToString(value);

  const handleChange = (changedField: IntakeField, nextValue: string | boolean) => {
    if (nextValue === "no" && currentValue !== "no" && optOutPromptEnabled) {
      setOptOutPromptOpen(true);
      return;
    }
    onChange(changedField, nextValue);
  };

  return (
    <div className="field-with-nudge">
      <FieldControl field={field} value={value} onChange={handleChange} showMissingRequired={showMissingRequired} />
      {currentValue === "no" && (
        <aside className="integration-nudge" role="note">
          <strong>연동은 언제든 다시 켜실 수 있습니다.</strong>
          <p>지금은 해제 상태로 저장됩니다. 오픈 이후라도 담당 매니저와 상의해 바로 연동하실 수 있습니다.</p>
        </aside>
      )}
      {optOutPromptOpen && (
        <GoogleBookingOptOutModal
          onKeep={() => setOptOutPromptOpen(false)}
          onOptOut={() => {
            setOptOutPromptOpen(false);
            onChange(field, "no");
          }}
        />
      )}
    </div>
  );
}

function GoogleBookingOptOutModal({ onKeep, onOptOut }: { onKeep: () => void; onOptOut: () => void }) {
  const keepButtonRef = useRef<HTMLButtonElement | null>(null);
  const { visitors, baselineRate, creatripRate } = GOOGLE_BOOKING_SIMULATION;
  const baselineBookings = Math.round(visitors * baselineRate);
  const creatripBookings = Math.round(visitors * creatripRate);
  const bookingMultiple = creatripBookings / baselineBookings;

  useEffect(() => {
    keepButtonRef.current?.focus();
  }, []);

  return (
    <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onKeep(); }}>
      <section
        className="booking-optout-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-optout-title"
        aria-describedby="booking-optout-lede"
        tabIndex={-1}
        onKeyDown={(event) => { if (event.key === "Escape") onKeep(); }}
      >
        <div className="booking-optout-head">
          <img className="booking-optout-character" src="/characters/tori-wait.png" alt="" aria-hidden="true" />
          <div>
            <p className="eyebrow">Google 예약 연동</p>
            <h3 id="booking-optout-title">소중한 구글 트래픽, 끄기 전에 전환율부터 비교해보세요</h3>
            <p id="booking-optout-lede">예약 버튼이 있고 없고의 문제가 아니라, 그 버튼이 어떤 페이지로 이어지느냐의 문제입니다.</p>
          </div>
          <button className="modal-close" type="button" aria-label="닫기" onClick={onKeep}>×</button>
        </div>

        <p className="booking-optout-basis">구글맵 유입 {visitors.toLocaleString("ko-KR")}명 기준</p>
        <div className="booking-optout-metrics">
          <article>
            <span>해제 시</span>
            <p className="booking-optout-figure">
              <strong>{formatPercent(baselineRate)}</strong>
              <em>(M사 공개 데이터 기준)</em>
            </p>
            <small>예약 {baselineBookings}건</small>
          </article>
          <article className="is-highlight">
            <span>유지 시</span>
            <p className="booking-optout-figure">
              <strong>{formatPercent(creatripRate)}</strong>
              <em>(크리에이트립 연동 업체 평균)</em>
            </p>
            <small>예약 {creatripBookings}건</small>
          </article>
        </div>

        <p className="booking-optout-conclusion">
          같은 트래픽인데 예약은 <strong>{bookingMultiple.toFixed(1)}배</strong> 차이납니다. 병의원처럼 꼼꼼히 따져보고 결정하는 상품일수록, 정보를 충분히 풀어 쓰고 맞는 상품을 짚어주는 콘텐츠에서 예약이 일어납니다.
        </p>

        <aside className="integration-nudge" role="note">
          <strong>고객이 쓰는 언어로 자동으로 바뀝니다</strong>
          <p>고객 휴대폰·PC가 어느 나라 말로 설정돼 있는지 알아보고, 영어·일본어·중국어 화면을 알아서 보여줍니다. 번역기를 돌리거나 언어를 고를 필요 없이, 처음 본 화면에서 바로 예약합니다.</p>
        </aside>

        <div className="booking-optout-actions">
          <button ref={keepButtonRef} className="button primary" type="button" onClick={onKeep}>연동 유지하기</button>
          <button className="button ghost" type="button" onClick={onOptOut}>그래도 해제하기</button>
        </div>
      </section>
    </div>
  );
}

function getExclusiveBenefitOptions(field: IntakeField): ExclusiveBenefitOption[] {
  const defaultByKey = new Map(EXCLUSIVE_BENEFIT_OPTIONS.map((option) => [option.key, option]));
  const options = field.options
    ?.map((option) => {
      const key = option.value as ExclusiveBenefitKey;
      const fallback = defaultByKey.get(key);
      return fallback ? { ...fallback, label: option.label, description: option.description ?? fallback.description } : null;
    })
    .filter((option): option is ExclusiveBenefitOption => Boolean(option));
  return options?.length ? options : EXCLUSIVE_BENEFIT_OPTIONS;
}

function getLanguageOptionSets(field: IntakeField): { baseLanguages: string[]; otherLanguages: string[]; statuses: string[]; defaultStatus: string } {
  const configOptions = field.options ?? [];
  const languageFromOption = (option: FieldOption) => option.value.replace(/^language:/, "");
  const statusFromOption = (option: FieldOption) => option.value.replace(/^status:/, "");
  const baseLanguages = configOptions.filter((option) => option.value.startsWith("language:") && option.description === "base").map(languageFromOption);
  const otherLanguages = configOptions.filter((option) => option.value.startsWith("language:") && option.description === "other").map(languageFromOption);
  const statuses = configOptions.filter((option) => option.value.startsWith("status:")).map(statusFromOption);
  const effectiveStatuses = statuses.length ? statuses : LANGUAGE_SUPPORT_STATUSES;
  return {
    baseLanguages: baseLanguages.length ? baseLanguages : BASE_LANGUAGE_OPTIONS,
    otherLanguages: otherLanguages.length ? otherLanguages : OTHER_LANGUAGE_OPTIONS,
    statuses: effectiveStatuses,
    defaultStatus: effectiveStatuses.includes(DEFAULT_LANGUAGE_SUPPORT_STATUS) ? DEFAULT_LANGUAGE_SUPPORT_STATUS : effectiveStatuses[0] ?? DEFAULT_LANGUAGE_SUPPORT_STATUS,
  };
}

function ExclusiveBenefitField({ field, value, onChange, showMissingRequired = false }: { field: IntakeField; value: unknown; onChange: (field: IntakeField, value: string | boolean) => void; showMissingRequired?: boolean }) {
  const entries = parseExclusiveBenefitValue(value);
  const benefitOptions = getExclusiveBenefitOptions(field);
  const describedBy = field.helperText ? `${field.id}-help` : undefined;

  const commitEntries = (nextEntries: Record<ExclusiveBenefitKey, ExclusiveBenefitEntry>) => {
    onChange(field, serializeExclusiveBenefitEntries(nextEntries));
  };

  const toggleOption = (key: ExclusiveBenefitKey, selected: boolean) => {
    commitEntries({ ...entries, [key]: { ...entries[key], selected } });
  };

  const updateText = (key: ExclusiveBenefitKey, text: string) => {
    commitEntries({ ...entries, [key]: { ...entries[key], selected: true, text } });
  };

  const isMissing = showMissingRequired && isRequiredFieldValueMissing(field, value);
  return (
    <div className={`field field-exclusiveBenefit${isMissing ? " field-missing-required" : ""}`} id={field.id} data-missing-target={field.id} tabIndex={-1} aria-invalid={isMissing || undefined}>
      <span id={`${field.id}-label`}>{field.label} {field.required && <em>필수</em>}</span>
      <div className="exclusive-benefit-list" role="group" aria-labelledby={`${field.id}-label`} aria-describedby={describedBy} aria-required={field.required} aria-invalid={isMissing || undefined}>
        {benefitOptions.map((option) => {
          const entry = entries[option.key];
          return (
            <article className="exclusive-benefit-card" key={option.key}>
              <label className="language-support-check">
                <input type="checkbox" checked={entry.selected} onChange={(event) => toggleOption(option.key, event.target.checked)} />
                <span>
                  <strong>{option.label}</strong>
                  <small>{option.description}</small>
                </span>
              </label>
              {entry.selected && (
                <textarea
                  value={entry.text}
                  className={isDefaultLikeValue(entry.text, option.defaultValue) ? "is-default-like" : undefined}
                  rows={3}
                  placeholder={option.placeholder}
                  onChange={(event) => updateText(option.key, event.target.value)}
                />
              )}
            </article>
          );
        })}
      </div>
      {field.helperText && <small id={describedBy}>{field.helperText}</small>}
    </div>
  );
}

function LanguageSupportField({ field, value, onChange, showMissingRequired = false }: { field: IntakeField; value: unknown; onChange: (field: IntakeField, value: string | boolean) => void; showMissingRequired?: boolean }) {
  const languageOptions = getLanguageOptionSets(field);
  const [customLanguage, setCustomLanguage] = useState("");
  const [customStatus, setCustomStatus] = useState(languageOptions.defaultStatus);
  const entries = parseLanguageSupportValue(value);
  const entryByLanguage = new Map(entries.map((entry) => [entry.language, entry]));
  const customEntries = entries.filter((entry) => entry.custom);
  const availableOtherLanguageOptions = languageOptions.otherLanguages.filter((language) => !entryByLanguage.has(language));
  const describedBy = field.helperText ? `${field.id}-help` : undefined;

  useEffect(() => {
    if (!languageOptions.statuses.includes(customStatus)) setCustomStatus(languageOptions.defaultStatus);
  }, [customStatus, languageOptions.defaultStatus, languageOptions.statuses]);

  const commitEntries = (nextEntries: LanguageSupportEntry[]) => onChange(field, formatLanguageSupportValue(nextEntries));

  const toggleLanguage = (language: string, checked: boolean) => {
    if (!checked) {
      commitEntries(entries.filter((entry) => entry.language !== language));
      return;
    }
    commitEntries([...entries.filter((entry) => entry.language !== language), { language, status: languageOptions.defaultStatus, custom: false }]);
  };

  const updateStatus = (language: string, status: string) => {
    commitEntries(entries.map((entry) => (entry.language === language ? { ...entry, status } : entry)));
  };

  const addCustomLanguage = () => {
    const language = customLanguage.trim();
    if (!language || entryByLanguage.has(language)) return;
    commitEntries([...entries, { language, status: customStatus, custom: true }]);
    setCustomLanguage("");
    setCustomStatus(languageOptions.defaultStatus);
  };

  const isMissing = showMissingRequired && isRequiredFieldValueMissing(field, value);
  return (
    <div className={`field field-languageSupport${isMissing ? " field-missing-required" : ""}`} id={field.id} data-missing-target={field.id} tabIndex={-1} aria-invalid={isMissing || undefined}>
      <span id={`${field.id}-label`}>{field.label} {field.required && <em>필수</em>}</span>
      <div className="language-support-list" role="group" aria-labelledby={`${field.id}-label`} aria-describedby={describedBy} aria-required={field.required} aria-invalid={isMissing || undefined}>
        {languageOptions.baseLanguages.map((language) => {
          const entry = entryByLanguage.get(language);
          return (
            <div className="language-support-row" key={language}>
              <label className="language-support-check">
                <input type="checkbox" checked={Boolean(entry)} onChange={(event) => toggleLanguage(language, event.target.checked)} />
                <span>{language}</span>
              </label>
              <select value={entry?.status ?? languageOptions.defaultStatus} onChange={(event) => updateStatus(language, event.target.value)} disabled={!entry}>
                {languageOptions.statuses.map((status) => <option key={status} value={status}>{status}</option>)}
              </select>
            </div>
          );
        })}
        {customEntries.map((entry) => (
          <div className="language-support-row custom" key={entry.language}>
            <strong>{entry.language}</strong>
            <select value={entry.status} onChange={(event) => updateStatus(entry.language, event.target.value)}>
              {languageOptions.statuses.map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
            <button type="button" onClick={() => commitEntries(entries.filter((candidate) => candidate.language !== entry.language))}>삭제</button>
          </div>
        ))}
        <div className="language-support-add-row">
          <select value={customLanguage} onChange={(event) => setCustomLanguage(event.target.value)}>
            <option value="">기타 언어 선택</option>
            {availableOtherLanguageOptions.map((language) => <option key={language} value={language}>{language}</option>)}
          </select>
          <select value={customStatus} onChange={(event) => setCustomStatus(event.target.value)}>
            {languageOptions.statuses.map((status) => <option key={status} value={status}>{status}</option>)}
          </select>
          <button className="button secondary compact" type="button" onClick={addCustomLanguage} disabled={!customLanguage}>추가</button>
        </div>
      </div>
      {field.helperText && <small id={describedBy}>{field.helperText}</small>}
    </div>
  );
}

const RESERVATION_DROPOFF_WARNING = "너무 많은 정보를 요청하면 예약 과정이 길어져 고객 이탈률이 높아질 수 있어요.";
const HIGHLIGHTED_HELPER_MESSAGES = [RESERVATION_DROPOFF_WARNING, REVIEW_BENEFIT_HELPER_TEXT];

function FieldHelperText({ id, text }: { id: string; text: string }) {
  const highlightedText = HIGHLIGHTED_HELPER_MESSAGES.find((message) => text.includes(message));
  if (!highlightedText) return <small id={id}>{text}</small>;

  const highlightedIndex = text.indexOf(highlightedText);
  const leadingText = text.slice(0, highlightedIndex).trim();
  const trailingText = text.slice(highlightedIndex + highlightedText.length).trim();
  return (
    <small id={id}>
      {leadingText}
      <strong className="field-helper-warning">{highlightedText}</strong>
      {trailingText && <span>{trailingText}</span>}
    </small>
  );
}

export function FieldControl({ field, value, onChange, showMissingRequired = false }: { field: IntakeField; value: unknown; onChange: (field: IntakeField, value: string | boolean) => void; showMissingRequired?: boolean }) {
  const describedBy = field.helperText ? `${field.id}-help` : undefined;
  const labelId = `${field.id}-label`;
  const isMissingRequired = showMissingRequired && isRequiredFieldValueMissing(field, value);
  const className = `field field-${field.type}${field.readOnly ? " field-readonly" : ""}${isMissingRequired ? " field-missing-required" : ""}`;
  const common = {
    id: field.id,
    name: field.id,
    "aria-describedby": describedBy,
    "aria-labelledby": labelId,
    "aria-invalid": isMissingRequired || undefined,
    required: field.required,
  };
  const labelContent = <>{field.label} {field.readOnly ? <em>고정</em> : field.required && <em>필수</em>}</>;
  const input = renderInput(field, value, onChange, common);
  const helper = field.helperText && describedBy ? <FieldHelperText id={describedBy} text={field.helperText} /> : null;

  if (field.type === "radio" || field.type === "multiCheckbox") {
    return (
      <div className={className} data-missing-target={field.id}>
        <span id={labelId}>{labelContent}</span>
        {input}
        {helper}
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div className={className} data-missing-target={field.id}>
        <label className="field-label" id={labelId} htmlFor={field.id}>{labelContent}</label>
        {input}
        {helper}
      </div>
    );
  }
  return (
    <label className={className} htmlFor={field.id} data-missing-target={field.id}>
      <span id={labelId}>{labelContent}</span>
      {input}
      {helper}
    </label>
  );
}

function renderInput(field: IntakeField, value: unknown, onChange: (field: IntakeField, value: string | boolean) => void, common: Record<string, unknown>) {
  if (field.readOnly) {
    const currentValue = fieldValueToString(value).trim();
    const fallbackValue = fieldValueToString(field.defaultValue).trim();
    const displayValue = currentValue || fallbackValue || "-";
    return (
      <output
        id={field.id}
        name={field.id}
        className="fixed-value"
        aria-describedby={common["aria-describedby"] as string | undefined}
      >
        <strong>{displayValue}{field.valueSuffix ?? ""}</strong>
        <span>변경 불가</span>
      </output>
    );
  }
  if (field.type === "date") {
    return (
      <DatePicker
        id={field.id}
        name={field.id}
        selected={parseDateValue(value)}
        onChange={(date: Date | null) => onChange(field, formatDateValue(date))}
        locale="ko"
        dateFormat="yyyy.MM.dd"
        placeholderText={field.placeholder ?? "날짜 선택"}
        isClearable
        showPopperArrow={false}
        className="date-picker-input"
        aria-describedby={common["aria-describedby"] as string | undefined}
        aria-invalid={common["aria-invalid"] ? "true" : undefined}
        required={Boolean(common.required)}
      />
    );
  }
  if (field.type === "dateRange") {
    const [startDate, endDate] = parseDateRangeValue(value);
    return (
      <DatePicker
        id={field.id}
        name={field.id}
        selected={startDate}
        startDate={startDate}
        endDate={endDate}
        onChange={(dates: [Date | null, Date | null]) => {
          const [start, end] = dates;
          onChange(field, formatDateRangeValue(start, end));
        }}
        selectsRange
        locale="ko"
        dateFormat="yyyy.MM.dd"
        placeholderText={field.placeholder ?? "날짜 범위 선택"}
        isClearable
        showPopperArrow={false}
        className="date-picker-input"
        aria-describedby={common["aria-describedby"] as string | undefined}
        aria-invalid={common["aria-invalid"] ? "true" : undefined}
        required={Boolean(common.required)}
      />
    );
  }
  if (field.type === "currency") {
    return (
      <NumericFormat
        id={field.id}
        name={field.id}
        className={isDefaultLikeValue(fieldValueToString(value), field.defaultValue) ? "is-default-like" : undefined}
        value={fieldValueToString(value)}
        thousandSeparator=","
        suffix="원"
        allowNegative={false}
        decimalScale={0}
        placeholder={field.placeholder}
        onValueChange={(values) => onChange(field, values.value)}
        aria-describedby={common["aria-describedby"] as string | undefined}
        aria-invalid={common["aria-invalid"] ? "true" : undefined}
        required={Boolean(common.required)}
      />
    );
  }
  if (field.type === "textarea") {
    const textValue = fieldValueToString(value);
    return (
      <textarea {...common} className={isDefaultLikeValue(textValue, field.defaultValue) ? "is-default-like" : undefined} rows={field.rows ?? 4} placeholder={field.placeholder} value={textValue} onChange={(event) => onChange(field, event.target.value)} />
    );
  }
  if (field.type === "select") {
    return (
      <select {...common} value={fieldValueToString(value)} onChange={(event) => onChange(field, event.target.value)}>
        <option value="">선택해주세요</option>
        {field.options?.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    );
  }
  if (field.type === "radio") {
    return (
      <div className="choice-grid" role="radiogroup" aria-labelledby={common["aria-labelledby"] as string} aria-required={Boolean(common.required)} aria-invalid={common["aria-invalid"] as boolean | undefined}>
        {field.options?.map((option) => (
          <label key={option.value} className="choice-card">
            <input type="radio" name={field.id} value={option.value} checked={fieldValueToString(value) === option.value} onChange={() => onChange(field, option.value)} required={Boolean(common.required)} aria-invalid={common["aria-invalid"] as boolean | undefined} />
            <span>
              <strong>{option.label}</strong>
              {option.description && <small>{option.description}</small>}
            </span>
          </label>
        ))}
      </div>
    );
  }
  if (field.type === "multiCheckbox") {
    const selectedValues = new Set(parseMultiCheckboxValue(value));
    return (
      <div className="multi-choice-grid" role="group" aria-labelledby={common["aria-labelledby"] as string} aria-required={Boolean(common.required)} aria-invalid={common["aria-invalid"] as boolean | undefined}>
        {field.options?.map((option) => (
          <label key={option.value} className="multi-choice-card">
            <input
              type="checkbox"
              checked={selectedValues.has(option.value)}
              onChange={(event) => onChange(field, toggleMultiCheckboxValue(value, option.value, event.target.checked, field.options))}
            />
            <span>
              <strong>{option.label}</strong>
              {option.description && <small>{option.description}</small>}
            </span>
          </label>
        ))}
      </div>
    );
  }
  if (field.type === "checkbox") {
    return (
      <label className="checkbox-line">
        <input type="checkbox" checked={value === true} onChange={(event) => onChange(field, event.target.checked)} required={Boolean(common.required)} aria-invalid={common["aria-invalid"] as boolean | undefined} />
        <span>{field.checkboxText ?? "확인했습니다"}</span>
      </label>
    );
  }
  return <input {...common} className={isDefaultLikeValue(fieldValueToString(value), field.defaultValue) ? "is-default-like" : undefined} type={field.type} min={field.min} max={field.max} step={field.step} placeholder={field.placeholder} value={fieldValueToString(value)} onChange={(event) => onChange(field, event.target.value)} />;
}

export function ProductSpreadsheetControl({
  repeater,
  value,
  contractType,
  categoryProfile,
  onAdd,
  onRemove,
  onReplace,
  files,
  uploadStatus,
  onUpload,
  onRemoveFile,
  onReorderFile,
  onRenameFile,
  showMissingRequired = false,
}: {
  repeater: RepeaterField;
  value: unknown;
  contractType: string;
  categoryProfile: IntakeCategoryProfile | null;
  onAdd: (repeater: RepeaterField) => void;
  onRemove: (repeater: RepeaterField, index: number) => void;
  onReplace: (repeater: RepeaterField, items: Record<string, unknown>[]) => void;
  files: UploadedFile[];
  uploadStatus: UploadStatus;
  onUpload: (definition: FileUploadDefinition, fileList: FileList | null) => void;
  onRemoveFile: (fileId: string) => void;
  onReorderFile: (definition: FileUploadDefinition, draggedFileId: string, targetFileId: string) => void;
  onRenameFile: (fileId: string, nextFileName: string) => void;
  showMissingRequired?: boolean;
}) {
  const requiredProductDetailCount = repeater.fields.filter((field) => field.required && PRODUCT_DETAIL_FIELDS.some((detailField) => detailField.key === field.path)).length;
  const hasRequiredProductDetails = requiredProductDetailCount > 0;
  const [activeCell, setActiveCell] = useState<{ row: number; key: ProductColumnKey } | null>(null);
  const [showProductDetails, setShowProductDetails] = useState(hasRequiredProductDetails);
  const pricingMode = getProductPricingMode(contractType);
  const productNoun = categoryProfile?.productNoun ?? "시술";
  const detailNoun = categoryProfile?.productDetailNoun ?? "시술별 상세내용";
  const repeaterFieldsByPath = new Map(repeater.fields.map((field) => [field.path, field]));
  const productTableColumns = PRODUCT_TABLE_COLUMNS
    .filter((column) => repeaterFieldsByPath.has(column.key))
    .map((column) => {
      const field = repeaterFieldsByPath.get(column.key);
      return { ...column, label: field?.label ?? column.label, placeholder: field?.placeholder ?? column.placeholder, required: Boolean(field?.required) };
    });
  const productDetailFields = PRODUCT_DETAIL_FIELDS
    .filter((field) => repeaterFieldsByPath.has(field.key))
    .map((field) => {
      const schemaField = repeaterFieldsByPath.get(field.key);
      return { ...field, label: schemaField?.label ?? field.label, placeholder: schemaField?.placeholder ?? field.placeholder, defaultValue: schemaField?.defaultValue ?? field.defaultValue, required: Boolean(schemaField?.required) };
    });
  const items = Array.isArray(value) ? (value as ProductOption[]) : [];
  const productRowsWithDetails = items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => isProductMeaningfullyFilled(item));
  const completedRows = productRowsWithDetails.length;
  const isMinRow = Boolean(repeater.minItems && items.length <= repeater.minItems);

  useEffect(() => {
    if (hasRequiredProductDetails) setShowProductDetails(true);
  }, [hasRequiredProductDetails]);

  const updateItems = (nextItems: ProductOption[]) => {
    onReplace(repeater, nextItems.map((item) => normalizeProductOptionPricing(item, pricingMode)) as unknown as Record<string, unknown>[]);
  };

  const updateCell = (rowIndex: number, key: ProductColumnKey, nextValue: string) => {
    const nextItems = items.map((item, itemIndex) => {
      if (itemIndex !== rowIndex) return item;
      return applyProductCellValue(item, key, nextValue, pricingMode);
    });
    updateItems(nextItems);
  };

  const updateDetailField = (rowIndex: number, key: ProductDetailKey, nextValue: string) => {
    updateItems(items.map((item, itemIndex) => (itemIndex === rowIndex ? { ...item, [key]: nextValue } : item)));
  };

  const handlePaste = (event: ReactClipboardEvent<HTMLInputElement | HTMLTextAreaElement>, rowIndex: number, key: ProductColumnKey) => {
    const text = event.clipboardData.getData("text/plain");
    if (!text.includes("\t") && !text.includes("\n")) return;

    event.preventDefault();
    let rows = text
      .replace(/\r/g, "")
      .split("\n")
      .filter((row) => row.length > 0)
      .map((row) => row.split("\t"));
    if (!rows.length) return;

    const pasteColumns = getProductPasteColumns(pricingMode, productTableColumns);
    const startColumnIndex = pasteColumns.findIndex((column) => column.key === key);
    if (startColumnIndex < 0) return;
    if (isProductHeaderRow(rows[0], pasteColumns, startColumnIndex)) rows = rows.slice(1);
    if (!rows.length) return;

    const nextItems = [...items];
    while (nextItems.length < rowIndex + rows.length) {
      nextItems.push(repeater.createItem() as ProductOption);
    }

    rows.forEach((cells, pastedRowIndex) => {
      const targetRowIndex = rowIndex + pastedRowIndex;
      let targetItem = nextItems[targetRowIndex] ?? (repeater.createItem() as ProductOption);
      cells.forEach((cell, cellOffset) => {
        const targetColumn = pasteColumns[startColumnIndex + cellOffset];
        if (!targetColumn) return;
        targetItem = applyProductCellValue(targetItem, targetColumn.key, cell.trim(), pricingMode);
      });
      nextItems[targetRowIndex] = targetItem;
    });

    updateItems(nextItems);
  };

  const handleCellKeyDown = (event: ReactKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>, rowIndex: number, column: ProductTableColumn) => {
    if (event.key !== "Enter" || event.shiftKey || column.multiline) return;
    event.preventDefault();
    const nextRow = rowIndex + 1;
    if (nextRow < items.length) {
      setActiveCell({ row: nextRow, key: column.key });
      return;
    }
    onAdd(repeater);
    window.requestAnimationFrame(() => setActiveCell({ row: nextRow, key: column.key }));
  };

  return (
    <section className="repeater-section product-sheet-section">
      <div className="repeater-head product-sheet-head">
        <div>
          <h3>{repeater.label}</h3>
          {repeater.description && <p>{repeater.description}</p>}
        </div>
        <button className="button secondary" type="button" onClick={() => onAdd(repeater)}>{repeater.addLabel}</button>
      </div>

      <div className="product-sheet-strategy-note">
        <strong>{withSubjectParticle(productNoun)} 많을수록 좋은 것은 아닙니다.</strong>
        <p>고객이 고르지 못하면 이탈이 크게 늘어납니다. 잘 판매될 핵심 {withObjectParticle(productNoun)} 선별해 구성·가격·추천 이유를 분명하게 적어주세요.</p>
      </div>

      <div className="product-sheet-toolbar" aria-live="polite">
        <span>{completedRows}개 상품 입력됨</span>
        <div className="product-sheet-toolbar-actions">
          <span>{getPricingModeDescription(pricingMode)}</span>
          <button className="button secondary compact" type="button" onClick={() => downloadProductSampleExcel(contractType, categoryProfile, productTableColumns)}>샘플 Excel 다운로드</button>
        </div>
      </div>

      <div className="product-sheet-hint">
        샘플 Excel을 다운로드해 가지고 계신 엑셀에서 상품 정보를 편리하게 작성한 뒤, 복사해서 가져오셔도 됩니다.
      </div>

      <div className="product-sheet-wrap">
        <table className="product-sheet-table">
          <colgroup>
            <col style={{ width: 44 }} />
            {productTableColumns.map((column) => <col key={column.key} style={{ width: column.width }} />)}
            <col style={{ width: 64 }} />
          </colgroup>
          <thead>
            <tr>
              <th aria-label="행 번호">No.</th>
              {productTableColumns.map((column, index) => {
                const state = getProductColumnState(column.key, pricingMode);
                return (
                  <th key={column.key} className={state.disabled ? "is-disabled" : undefined}>
                    <span className="sheet-column-letter">{String.fromCharCode(65 + index)}</span>
                    {column.label} {column.required && <em>필수</em>}
                  </th>
                );
              })}
              <th aria-label="행 작업" />
            </tr>
          </thead>
          <tbody>
            {items.map((item, rowIndex) => (
              <tr key={rowIndex}>
                <td className="sheet-row-number">{rowIndex + 1}</td>
                {productTableColumns.map((column) => {
                  const state = getProductColumnState(column.key, pricingMode);
                  const cellValue = getProductCellValue(item, column.key, pricingMode);
                  const isActive = activeCell?.row === rowIndex && activeCell.key === column.key;
                  const cellId = productFieldFocusId(rowIndex, column.key);
                  const shouldValidateRow = isProductMeaningfullyFilled(item) || (completedRows === 0 && rowIndex === 0 && column.key === "optionName");
                  const isRequiredForRow = shouldValidateRow && column.required;
                  const isMissingRequired = showMissingRequired && isRequiredForRow && !cellValue.trim();
                  return (
                    <td key={column.key} className={`${state.disabled ? "is-disabled" : ""} ${isActive ? "is-active" : ""}${isMissingRequired ? " product-cell-missing-required" : ""}`} data-missing-target={cellId} onClick={() => !state.disabled && setActiveCell({ row: rowIndex, key: column.key })} aria-invalid={isMissingRequired || undefined}>
                      {column.key === "freeReservationLabel" ? (
                        <div className="sheet-static-cell">{cellValue || "무료예약"}</div>
                      ) : column.multiline ? (
                        <textarea
                          id={cellId}
                          value={cellValue}
                          placeholder={column.placeholder}
                          disabled={state.disabled}
                          rows={2}
                          onChange={(event) => updateCell(rowIndex, column.key, event.target.value)}
                          onFocus={() => setActiveCell({ row: rowIndex, key: column.key })}
                          onPaste={(event) => handlePaste(event, rowIndex, column.key)}
                          onKeyDown={(event) => handleCellKeyDown(event, rowIndex, column)}
                          autoFocus={isActive}
                          required={isRequiredForRow}
                          aria-invalid={isMissingRequired || undefined}
                        />
                      ) : (
                        <input
                          id={cellId}
                          type="text"
                          inputMode={column.numeric ? "numeric" : undefined}
                          value={cellValue}
                          placeholder={state.disabled ? "비활성" : column.placeholder}
                          disabled={state.disabled}
                          onChange={(event) => updateCell(rowIndex, column.key, event.target.value)}
                          onFocus={() => setActiveCell({ row: rowIndex, key: column.key })}
                          onPaste={(event) => handlePaste(event, rowIndex, column.key)}
                          onKeyDown={(event) => handleCellKeyDown(event, rowIndex, column)}
                          autoFocus={isActive}
                          required={isRequiredForRow}
                          aria-invalid={isMissingRequired || undefined}
                        />
                      )}
                    </td>
                  );
                })}
                <td className="sheet-row-actions">
                  <button className="sheet-delete-button" type="button" onClick={() => onRemove(repeater, rowIndex)} disabled={isMinRow} aria-label={`${rowIndex + 1}번 상품 삭제`}>삭제</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="product-sheet-footer">
        <button className="button secondary" type="button" onClick={() => onAdd(repeater)}>행 추가</button>
        <span>Tab으로 다음 셀 이동 · Enter로 아래 행 이동 · 가로 스크롤로 전체 컬럼 확인</span>
      </div>

      <div className="product-detail-cta">
        <div>
          <strong>{hasRequiredProductDetails ? `${detailNoun}에 필수 입력 항목이 있어요.` : `${detailNoun}을 추가로 작성할 수 있어요.`}</strong>
          <p>{hasRequiredProductDetails ? `상품표를 작성한 뒤 각 ${productNoun}의 '필수' 표시 항목을 모두 입력해주세요. 고객이 상세페이지에서 아이템을 선택하는 데 도움이 됩니다.` : `엑셀표를 먼저 완성한 뒤, 설명이 더 필요한 ${productNoun}만 추가로 작성해주세요. 고객이 상세페이지에서 아이템을 선택하는 데 도움이 됩니다.`}</p>
        </div>
        <button className="button secondary" type="button" onClick={() => setShowProductDetails((current) => !current)} disabled={hasRequiredProductDetails || (productRowsWithDetails.length === 0 && !showProductDetails)}>
          {hasRequiredProductDetails ? `${detailNoun} 필수 작성` : showProductDetails ? `${detailNoun} 접기` : `${detailNoun} 입력`}
        </button>
      </div>

      {showProductDetails && (
        <section className="product-detail-section" aria-label={detailNoun}>
          <div className="product-detail-head">
            <span>{hasRequiredProductDetails ? `필수 ${requiredProductDetailCount}개` : "추가 정보"}</span>
            <strong>{detailNoun}</strong>
            <p>{hasRequiredProductDetails ? `각 ${productNoun}마다 필수로 표시된 상세 정보를 입력해주세요.` : `예약페이지에 더 정확히 설명해야 하는 ${productNoun}만 간결하게 적어주세요.`}</p>
          </div>
          {productRowsWithDetails.length > 0 ? (
            <div className="product-detail-list">
              {productRowsWithDetails.map(({ item, index }) => {
                const missingRequiredDetailCount = productDetailFields.filter((field) => field.required && !fieldValueToString(item[field.key]).trim()).length;
                const basePhotoDefinition = createProductDetailPhotoUploadDefinition(index, item);
                const photoDefinition = {
                  ...basePhotoDefinition,
                  label: fieldValueToString(item.optionName).trim() ? basePhotoDefinition.label : `${productNoun} ${index + 1} 관련 사진`,
                  description: `이 ${productNoun} 설명에 함께 보여주면 좋은 진행 장면, 도구, 결과 예시 사진이 있다면 첨부해주세요.`,
                };
                return (
                  <details className="product-detail-card" key={`product-detail-${index}`} open={hasRequiredProductDetails}>
                    <summary>
                      <span>{index + 1}. {fieldValueToString(item.optionName).trim() || "상품명 미입력"}</span>
                      <em>{missingRequiredDetailCount > 0 ? `필수 ${missingRequiredDetailCount}개 남음` : hasRequiredProductDetails ? "필수 작성 완료" : "추가 작성"}</em>
                    </summary>
                    <div className="product-detail-fields">
                      {productDetailFields.map((field) => {
                        const fieldValue = fieldValueToString(item[field.key]);
                        const isMissingRequired = showMissingRequired && field.required && !fieldValue.trim();
                        const fieldId = productFieldFocusId(index, field.key);
                        return (
                          <div className={`field${isMissingRequired ? " field-missing-required" : ""}`} key={field.key} data-missing-target={fieldId}>
                            <label className="field-label" htmlFor={fieldId}>{field.label} {field.required && <em>필수</em>}</label>
                            <textarea
                              id={fieldId}
                              rows={3}
                              value={fieldValue}
                              className={isDefaultLikeValue(fieldValue, field.defaultValue) ? "is-default-like" : undefined}
                              placeholder={field.placeholder}
                              onChange={(event) => updateDetailField(index, field.key, event.target.value)}
                              required={field.required}
                              aria-invalid={isMissingRequired || undefined}
                            />
                          </div>
                        );
                      })}
                    </div>
                    <div className="product-detail-photo-upload">
                      <FileUploadCard
                        definition={photoDefinition}
                        files={files.filter((file) => isUploadedFileForDefinition(file, photoDefinition))}
                        status={uploadStatus[photoDefinition.id]}
                        onUpload={onUpload}
                        onRemove={onRemoveFile}
                        onReorder={onReorderFile}
                        onRename={onRenameFile}
                      />
                    </div>
                  </details>
                );
              })}
            </div>
          ) : (
            <p className="product-detail-empty">상품표에 옵션명을 입력하면 {hasRequiredProductDetails ? "필수 상세 항목을" : detailNoun} 작성할 수 있어요.</p>
          )}
        </section>
      )}
    </section>
  );
}

function RepeaterControl({
  repeater,
  value,
  onAdd,
  onRemove,
  onChange,
  aiLoadingKey,
  onAiFill,
  categoryProfile,
  hiddenFieldIds,
  files = [],
  uploadStatus = {},
  onUpload,
  onRemoveFile,
  onReorderFile,
  onRenameFile,
  showMissingRequired = false,
}: {
  repeater: RepeaterField;
  value: unknown;
  onAdd: (repeater: RepeaterField) => void;
  onRemove: (repeater: RepeaterField, index: number) => void;
  onChange: (repeater: RepeaterField, index: number, itemPath: string, value: string | boolean) => void;
  aiLoadingKey?: string | null;
  onAiFill?: (action: AiFillAction, context?: { repeaterId?: string; itemIndex?: number }) => void;
  categoryProfile?: IntakeCategoryProfile | null;
  hiddenFieldIds?: string[];
  files?: UploadedFile[];
  uploadStatus?: UploadStatus;
  onUpload?: (definition: FileUploadDefinition, fileList: FileList | null) => void;
  onRemoveFile?: (fileId: string) => void;
  onReorderFile?: (definition: FileUploadDefinition, draggedFileId: string, targetFileId: string) => void;
  onRenameFile?: (fileId: string, nextFileName: string) => void;
  showMissingRequired?: boolean;
}) {
  const hiddenFields = new Set(hiddenFieldIds ?? []);
  const visibleFields = repeater.fields.filter((field) => !hiddenFields.has(field.id));
  const items = Array.isArray(value) ? value : [];
  const showAddButtonAtBottom = repeater.id === "doctors";
  return (
    <section className="repeater-section">
      <div className="repeater-head">
        <div>
          <h3>{repeater.label}</h3>
          {repeater.description && <p>{repeater.description}</p>}
        </div>
        {!showAddButtonAtBottom && <button className="button secondary" type="button" onClick={() => onAdd(repeater)}>{repeater.addLabel}</button>}
      </div>
      <div className="repeater-list">
        {items.map((item, index) => (
          <article className="repeater-card" key={index}>
            <div className="repeater-card-head">
              <strong>{repeater.label} {index + 1}</strong>
              <button className="button ghost danger" type="button" onClick={() => onRemove(repeater, index)} disabled={Boolean(repeater.minItems && items.length <= repeater.minItems)}>삭제</button>
            </div>
            {repeater.id === "doctors" && onUpload && onRemoveFile && (() => {
              const baseDefinition = createDoctorPhotoUploadDefinition(index, item);
              const expertNoun = categoryProfile?.expertNoun ?? "의료진 / 전문가";
              const doctorName = fieldValueToString((item as Record<string, unknown>).name).trim();
              const definition = {
                ...baseDefinition,
                label: doctorName ? baseDefinition.label : `${expertNoun} ${index + 1} 사진`,
                description: `해당 ${expertNoun} 소개에 사용할 대표 사진 1장을 첨부해주세요.`,
              };
              return (
                <div className="doctor-photo-upload">
                  <FileUploadCard
                    definition={definition}
                    files={files.filter((file) => isUploadedFileForDefinition(file, definition))}
                    status={uploadStatus[definition.id]}
                    onUpload={onUpload}
                    onRemove={onRemoveFile}
                    onReorder={onReorderFile}
                    onRename={onRenameFile}
                  />
                </div>
              );
            })()}
            <GroupedFieldControls
              fields={visibleFields as IntakeField[]}
              groups={REPEATER_FIELD_GROUPS[repeater.id]}
              aiLoadingKey={aiLoadingKey}
              aiContext={{ repeaterId: repeater.id, itemIndex: index }}
              onAiFill={onAiFill}
              renderField={(field) => {
                const fullField: IntakeField = { ...field, path: field.path, id: `${repeater.id}-${index}-${field.id}` };
                const itemValue = (item as Record<string, unknown>)[field.path];
                return (
                  <FieldControl
                    key={field.id}
                    field={fullField}
                    value={itemValue}
                    onChange={(_, nextValue) => onChange(repeater, index, field.path, nextValue)}
                    showMissingRequired={showMissingRequired}
                  />
                );
              }}
            />
          </article>
        ))}
      </div>
      {showAddButtonAtBottom && (
        <div className="repeater-footer">
          <button className="button secondary" type="button" onClick={() => onAdd(repeater)}>{repeater.addLabel}</button>
        </div>
      )}
    </section>
  );
}

function PhotoDisplayGuide() {
  return (
    <aside className="photo-display-guide" aria-label="사진 노출 예시">
      <div>
        <span>사진 노출 예시</span>
        <strong>첨부한 정적인 사진은 데스크톱·모바일에서 이렇게 배치됩니다.</strong>
        <p>대표사진은 크게, 나머지 사진은 갤러리로 노출돼요. 아래 예시는 같은 크기로 맞춰 보여드립니다.</p>
      </div>
      <div className="photo-static-examples">
        <figure>
          <img src="/examples/photo-static-detail-layout.png" alt="정적인 사진이 상품 상세 상단 갤러리에 배치되는 예시" loading="lazy" />
          <figcaption>상품 상세 상단 갤러리</figcaption>
        </figure>
        <figure>
          <img src="/examples/photo-static-list-layout.png" alt="정적인 대표사진이 상품 리스트 카드에 배치되는 예시" loading="lazy" />
          <figcaption>상품 리스트 대표사진</figcaption>
        </figure>
      </div>
      <details className="photo-gif-example">
        <summary>대표사진을 GIF로 올렸을 때 예시 보기</summary>
        <img src="/examples/photo-display-layout.gif" alt="대표사진을 GIF로 올렸을 때 상품 페이지에서 보이는 예시" loading="lazy" />
        <p>대표사진을 GIF로 첨부하면 같은 위치에서 움직이는 이미지로 노출될 수 있습니다.</p>
      </details>
    </aside>
  );
}

function BeforeAfterPhotoDisplayGuide() {
  return (
    <div className="before-after-guide" aria-label="전후 비교 사진 노출 예시">
      <article>
        <span>전/후 낱장 업로드</span>
        <strong>같은 위치·구도로 올리면 비교 슬라이더 제공</strong>
        <div className="ba-split-preview" aria-hidden="true">
          <div className="ba-half before">Before</div>
          <div className="ba-half after">After</div>
          <div className="ba-divider"><span>‹›</span></div>
        </div>
      </article>
      <article>
        <span>전후가 한 장에 있는 이미지</span>
        <strong>한 장 그대로 전체 슬라이더에 노출</strong>
        <div className="ba-single-preview" aria-hidden="true">
          <button type="button" tabIndex={-1}>‹</button>
          <div>
            <small>Before</small>
            <small>After</small>
          </div>
          <button type="button" tabIndex={-1}>›</button>
          <em>3 / 14</em>
        </div>
      </article>
    </div>
  );
}

function HeroImageDisplayGuide() {
  return (
    <div className="photo-guide-grid" aria-label="대표사진 노출 및 세이프존 안내">
      <article className="photo-guide-card">
        <span>권장 비율 1200×935</span>
        <strong>세로 사진은 좌우가 크게 잘릴 수 있어요</strong>
        <div className="vertical-crop-preview" aria-hidden="true">
          <div className="vertical-source"><span>세로 원본</span></div>
          <div className="crop-window"><span>실제 노출 영역</span></div>
        </div>
      </article>
      <article className="photo-guide-card">
        <span>Main photo safe zone</span>
        <strong>중요한 얼굴·글씨는 가장자리 130px 안쪽에 배치해주세요</strong>
        <div className="safe-zone-preview" aria-hidden="true">
          <div className="safe-zone-frame"><span>Safe Zone</span></div>
          <i className="safe-top">130px</i>
          <i className="safe-left">130px</i>
        </div>
      </article>
    </div>
  );
}

function ListingPhotoLayoutGuide() {
  return (
    <div className="listing-layout-guide" aria-label="상품 페이지 등재 사진 노출 예시">
      <article>
        <span>Desktop</span>
        <strong>왼쪽 큰 사진은 대표사진입니다. 오른쪽에는 보조사진 4개만 먼저 보이고, VIEW ALL을 누르면 최대 30개를 볼 수 있습니다.</strong>
        <div className="desktop-gallery-preview" aria-hidden="true">
          <div className="gallery-main is-hero"><b>대표사진<br />별도 업로드</b></div>
          <div className="gallery-thumb one is-listing" />
          <div className="gallery-thumb two is-listing" />
          <div className="gallery-thumb three is-listing" />
          <div className="gallery-thumb four is-listing"><em>VIEW ALL · 최대 30</em></div>
        </div>
      </article>
      <article>
        <span>Mobile</span>
        <strong>모바일 1번 사진은 대표사진입니다. 2번 이후 사진은 계속 넘겨보거나 전체보기에서 최대 30개까지 확인합니다.</strong>
        <div className="mobile-gallery-preview" aria-hidden="true">
          <div className="mobile-topbar" />
          <div className="mobile-photo is-hero"><b>1번 대표사진</b></div>
          <div className="mobile-listing-strip">
            <i>2</i><i>3</i><i>4</i><em>등재 사진</em>
          </div>
        </div>
      </article>
    </div>
  );
}

function PhotoPlacementPreview({ files }: { files: UploadedFile[] }) {
  const heroFile = files.find((file) => file.category === "hero_image" && file.contentType.startsWith("image/"));
  const allListingFiles = files.filter((file) => file.category === "listing_photo" && file.contentType.startsWith("image/"));
  const listingFiles = allListingFiles.slice(0, 4);
  const hiddenPhotoCount = Math.max(0, allListingFiles.length - listingFiles.length);
  const slots = [
    { label: "대표사진", file: heroFile, className: "is-hero" },
    ...Array.from({ length: 4 }, (_, index) => ({ label: `보조사진 ${index + 1}`, file: listingFiles[index], className: "" })),
  ];

  return (
    <aside className="photo-placement-preview" aria-label="대표사진과 상위 등재사진 배치 미리보기">
      <div>
        <span>배치 미리보기</span>
        <strong>상단 미리보기는 1 + 4장, 전체보기는 최대 {MAX_LISTING_PHOTOS}장</strong>
        <p>보조사진이 4장보다 많아도 사라지지 않습니다. 상단에는 첫 4장만 보이고 나머지는 전체보기 갤러리에 이어서 노출돼요. 현재 {allListingFiles.length}장을 첨부했습니다.</p>
      </div>
      <div className="placement-line">
        {slots.map((slot, index) => (
          <figure className={`placement-slot ${slot.className}`} key={`${slot.label}-${index}`}>
            {slot.className === "is-hero" ? (
              <div className="placement-hero-crop">
                {slot.file ? <img src={slot.file.downloadPath} alt="" loading="lazy" /> : <div className="placement-empty" />}
                {slot.file && (
                  <>
                    <i className="crop-risk top" />
                    <i className="crop-risk right" />
                    <i className="crop-risk bottom" />
                    <i className="crop-risk left" />
                    <span>가장자리 잘림 가능</span>
                  </>
                )}
              </div>
            ) : slot.file ? (
              <img src={slot.file.downloadPath} alt="" loading="lazy" />
            ) : (
              <div className="placement-empty" />
            )}
            <figcaption>{slot.label}{index === 4 && hiddenPhotoCount > 0 ? ` · +${hiddenPhotoCount}장 더보기` : ""}</figcaption>
          </figure>
        ))}
      </div>
      <HeroCropAreaPreview file={heroFile} />
    </aside>
  );
}

function HeroCropAreaPreview({ file }: { file?: UploadedFile }) {
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    setImageSize(null);
  }, [file?.id]);

  const cropBox = imageSize ? (() => {
    const imageRatio = imageSize.width / imageSize.height;
    if (imageRatio > HERO_IMAGE_LIST_CARD_VISIBLE_RATIO) {
      const width = (HERO_IMAGE_LIST_CARD_VISIBLE_RATIO / imageRatio) * 100;
      return { left: (100 - width) / 2, top: 0, width, height: 100 };
    }
    const height = (imageRatio / HERO_IMAGE_LIST_CARD_VISIBLE_RATIO) * 100;
    return { left: 0, top: (100 - height) / 2, width: 100, height };
  })() : null;

  return (
    <details className="photo-gif-example hero-crop-area-preview-collapse">
      <summary>대표사진 잘림 예측 보기</summary>
      <div className="hero-crop-area-preview">
        <div>
          <span>대표사진 잘림 예측</span>
          <strong>초록 박스 안쪽이 주로 노출돼요</strong>
          <p>상품 리스트 카드에서 실제로 보이는 비율 기준의 중앙 크롭 예측입니다. 상세 페이지 권장 비율은 {Math.round(HERO_IMAGE_DETAIL_TARGET_RATIO * 935)}×935입니다.</p>
        </div>
        {file ? (
          <div className="crop-source-frame" style={imageSize ? { aspectRatio: `${imageSize.width} / ${imageSize.height}` } : undefined}>
            <img
              src={file.downloadPath}
              alt=""
              loading="lazy"
              onLoad={(event) => setImageSize({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })}
            />
            {cropBox && (
              <div
                className="crop-visible-box"
                style={{ left: `${cropBox.left}%`, top: `${cropBox.top}%`, width: `${cropBox.width}%`, height: `${cropBox.height}%` }}
              >
                <span>노출 예상 영역</span>
              </div>
            )}
          </div>
        ) : (
          <div className="crop-source-empty">대표사진을 첨부하면 잘림 예상 영역이 표시됩니다.</div>
        )}
      </div>
    </details>
  );
}

function FileUploadCard({
  definition,
  files,
  status,
  onUpload,
  onRemove,
  onReorder,
  onRename,
  showMissingRequired = false,
}: {
  definition: FileUploadDefinition;
  files: UploadedFile[];
  status: UploadState;
  onUpload: (definition: FileUploadDefinition, fileList: FileList | null) => void;
  onRemove: (fileId: string) => void;
  onReorder?: (definition: FileUploadDefinition, draggedFileId: string, targetFileId: string) => void;
  onRename?: (fileId: string, nextFileName: string) => void;
  showMissingRequired?: boolean;
}) {
  const inputId = `upload-${definition.id}`;
  const reachedMaxFiles = Boolean(definition.maxFiles && files.length >= definition.maxFiles);
  const isMissingRequired = showMissingRequired && Boolean(definition.required) && files.length === 0;
  const [isDragging, setIsDragging] = useState(false);
  const [draggingFileId, setDraggingFileId] = useState<string | null>(null);
  const [dragOverFileId, setDragOverFileId] = useState<string | null>(null);
  const canReorderFiles = Boolean(onReorder && files.length > 1);

  const preventDragDefault = (event: ReactDragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragOver = (event: ReactDragEvent<HTMLLabelElement>) => {
    preventDragDefault(event);
    if (reachedMaxFiles) return;
    event.dataTransfer.dropEffect = "copy";
    setIsDragging(true);
  };

  const handleDragLeave = (event: ReactDragEvent<HTMLLabelElement>) => {
    preventDragDefault(event);
    setIsDragging(false);
  };

  const handleDrop = (event: ReactDragEvent<HTMLLabelElement>) => {
    preventDragDefault(event);
    setIsDragging(false);
    if (reachedMaxFiles) return;
    onUpload(definition, event.dataTransfer.files);
  };

  const handlePreviewDragStart = (event: ReactDragEvent<HTMLElement>, fileId: string) => {
    if (!canReorderFiles) return;
    setDraggingFileId(fileId);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", fileId);
  };

  const handlePreviewDragOver = (event: ReactDragEvent<HTMLElement>, fileId: string) => {
    if (!canReorderFiles) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDragOverFileId(fileId);
  };

  const handlePreviewDrop = (event: ReactDragEvent<HTMLElement>, targetFileId: string) => {
    if (!canReorderFiles) return;
    event.preventDefault();
    const draggedFileId = event.dataTransfer.getData("text/plain") || draggingFileId;
    if (draggedFileId && draggedFileId !== targetFileId) onReorder?.(definition, draggedFileId, targetFileId);
    setDraggingFileId(null);
    setDragOverFileId(null);
  };

  const handlePreviewDragEnd = () => {
    setDraggingFileId(null);
    setDragOverFileId(null);
  };

  const uploadHint = definition.maxFiles
    ? `클릭 또는 드래그해서 첨부 · 최대 ${definition.maxFiles}개 · 현재 ${files.length}개 · ${uploadLimitDescription(definition.accept)}`
    : definition.multiple
      ? `클릭 또는 드래그해서 첨부 · 여러 파일 가능 · ${uploadLimitDescription(definition.accept)}`
      : `클릭 또는 드래그해서 첨부 · 파일 1개 · ${uploadLimitDescription(definition.accept)}`;

  return (
    <article className={`upload-card${isMissingRequired ? " field-missing-required" : ""}`}>
      <div>
        <h3>{definition.label} {definition.required && <em>필수</em>}</h3>
        {definition.description && <p>{definition.description}</p>}
        {definition.category === "listing_photo" && (
          <div className="gallery-upload-progress" role="status" aria-label={`전체보기 갤러리 자료 ${files.length}개, 최대 ${MAX_LISTING_PHOTOS}개`}>
            <div><strong>{files.length} / {MAX_LISTING_PHOTOS}개</strong><span>{files.length >= 10 ? "상품을 충분히 둘러볼 수 있는 갤러리가 만들어졌어요." : files.length > 4 ? `좋아요. ${10 - files.length}개만 더 올리면 권장 10개를 채워요.` : "상단 4개 이후 자료도 전체보기에 모두 노출돼요. 사진·영상을 합쳐 10개 이상을 권장합니다."}</span></div>
            <progress max={MAX_LISTING_PHOTOS} value={files.length} />
          </div>
        )}
      </div>
      <label
        className={`upload-drop${isDragging ? " is-dragging" : ""}`}
        htmlFor={inputId}
        onDragEnter={handleDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          id={inputId}
          type="file"
          accept={definition.accept}
          multiple={definition.multiple}
          disabled={reachedMaxFiles}
          required={definition.required}
          aria-invalid={isMissingRequired || undefined}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onUpload(definition, event.target.files)}
        />
        <strong>{status === "uploading" ? "첨부 중..." : reachedMaxFiles ? "최대 장수 첨부 완료" : "파일 선택하기"}</strong>
        <span>{uploadHint}</span>
      </label>
      {definition.category === "hero_image" && <HeroImageDisplayGuide />}
      {definition.category === "listing_photo" && <ListingPhotoLayoutGuide />}
      {definition.category === "before_after_photo" && <BeforeAfterPhotoDisplayGuide />}
      {typeof status === "object" && (
        <div className="upload-error" role="alert">
          <strong>{status.title}</strong>
          <p>{status.description}</p>
        </div>
      )}
      {files.length > 0 && (
        <div className="file-preview-area">
          {canReorderFiles && <p className="file-reorder-hint">썸네일을 드래그해서 순서를 바꿀 수 있어요.</p>}
          <div className={`file-preview-strip${definition.maxFiles === 1 ? " is-single" : ""}`} aria-label={`${definition.label} 첨부 파일 미리보기`}>
            {files.map((file) => {
              const { baseName, extension } = splitFileName(file.fileName);
              const renameInputId = `rename-${definition.id}-${file.id}`;
              return (
                <article
                  className={`file-preview-card${draggingFileId === file.id ? " is-dragging" : ""}${dragOverFileId === file.id && draggingFileId !== file.id ? " is-drop-target" : ""}`}
                  key={file.id}
                  draggable={canReorderFiles}
                  title={canReorderFiles ? "드래그해서 순서를 바꿀 수 있어요" : undefined}
                  onDragStart={(event) => handlePreviewDragStart(event, file.id)}
                  onDragOver={(event) => handlePreviewDragOver(event, file.id)}
                  onDragLeave={() => setDragOverFileId((current) => (current === file.id ? null : current))}
                  onDrop={(event) => handlePreviewDrop(event, file.id)}
                  onDragEnd={handlePreviewDragEnd}
                >
                  <div className="file-preview-thumb" aria-hidden="true">
                    {file.contentType.startsWith("image/") ? <img src={file.downloadPath} alt="" loading="lazy" /> : <span>FILE</span>}
                    {canReorderFiles && <span className="file-drag-handle">⋮⋮ 드래그</span>}
                  </div>
                  <div className="file-preview-meta">
                    <label className="file-rename-field" htmlFor={renameInputId} onPointerDown={(event) => event.stopPropagation()} onDragStart={(event) => event.stopPropagation()}>
                      <input
                        id={renameInputId}
                        type="text"
                        value={baseName}
                        aria-label={`${file.fileName} 파일명 변경`}
                        onChange={(event) => {
                          const nextBaseName = sanitizeFileBaseName(event.target.value);
                          if (!nextBaseName.trim()) return;
                          onRename?.(file.id, `${nextBaseName}${extension}`);
                        }}
                      />
                      {extension && <span>{extension}</span>}
                    </label>
                    <span>{formatBytes(file.size)} · {formatDateTime(file.uploadedAt)}</span>
                  </div>
                  <div className="file-actions">
                    <a href={file.downloadPath} target="_blank" rel="noreferrer">확인</a>
                    <button type="button" onClick={() => onRemove(file.id)}>제거</button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      )}
    </article>
  );
}

function ReviewPanel({
  draft,
  steps,
  onGoToStep,
  onGoToMissingField,
  onGoToCategories,
  onSubmit,
  submissionDisabled,
}: {
  draft: DraftState;
  steps: IntakeStep[];
  onGoToStep: (step: number) => void;
  onGoToMissingField: (item: MissingRequiredItem) => void;
  onGoToCategories: () => void;
  onSubmit: () => void;
  submissionDisabled: boolean;
}) {
  const missing = collectMissingRequiredItemsFromSteps(draft, steps);
  const productCount = getEffectiveProducts(draft.data.products).length;
  const docCount = draft.files.filter((file) => file.category.includes("registration") || file.category.includes("certificate") || file.category.includes("bankbook") || file.category.includes("document")).length;
  const photoCount = draft.files.length - docCount;
  const categoryProfile = getCategoryProfile(draft.data.category.categoryId);
  return (
    <section className="review-grid">
      <article className="review-card">
        <span>상품 유형</span>
        <strong>{draft.data.category.label || "유형 미선택"}</strong>
        <p>{categoryProfile ? categoryProfile.domainLabel : "첫 화면에서 상품 유형을 선택해주세요."}</p>
        <button className="link-button" type="button" onClick={onGoToCategories}>수정하기</button>
      </article>
      <article className="review-card">
        <span>업체</span>
        <strong>{draft.data.company.storeNameKo || "업체명 미입력"}</strong>
        <p>{draft.data.company.contactName || "담당자 미입력"} · {draft.data.company.contactEmail || "이메일 미입력"}</p>
        <button className="link-button" type="button" onClick={() => onGoToStep(stepIndexById("company", steps))}>수정하기</button>
      </article>
      {steps.some((candidate) => candidate.id === "contract") && (
        <article className="review-card">
          <span>계약 구조</span>
          <strong>{contractTypeLabel(draft.data.contract.contractType, sanitizeUpsellRate(draft.data.contract.upsellCommissionRate) || FIXED_UPSELL_COMMISSION_RATE)}</strong>
          <p>{contractTypeSummary(draft.data.contract.contractType, sanitizeUpsellRate(draft.data.contract.upsellCommissionRate) || FIXED_UPSELL_COMMISSION_RATE)}</p>
          <button className="link-button" type="button" onClick={() => onGoToStep(stepIndexById("contract", steps))}>수정하기</button>
        </article>
      )}
      <article className="review-card">
        <span>상품</span>
        <strong>상품 {productCount}개</strong>
        <p>{draft.data.content.catchphrase || "캐치프레이즈 미입력"}</p>
        <button className="link-button" type="button" onClick={() => onGoToStep(stepIndexById("products", steps))}>수정하기</button>
      </article>
      <article className="review-card">
        <span>첨부 자료</span>
        <strong>사진 {Math.max(photoCount, 0)}개 · 서류 {docCount}개</strong>
        <p>제출 전 필요한 자료가 모두 첨부되었는지 확인해주세요.</p>
        <button className="link-button" type="button" onClick={() => onGoToStep(stepIndexById("photos", steps))}>수정하기</button>
      </article>
      {missing.length > 0 ? (
        <article className="review-card warning wide">
          <span>필수 항목 누락</span>
          <strong>{missing.length}개 항목을 더 채워주세요</strong>
          <p>아래 항목을 누르면 입력해야 할 위치로 바로 이동합니다.</p>
          <div className="missing-field-list" aria-label="누락된 필수 항목">
            {missing.map((item) => (
              <button className="missing-field-chip" type="button" key={item.key} onClick={() => onGoToMissingField(item)}>
                <strong>{item.label}</strong>
                <span>{item.stepTitle}</span>
              </button>
            ))}
          </div>
        </article>
      ) : (
        <article className="review-card success wide">
          <span>제출 준비 완료</span>
          <strong>필수 항목이 모두 채워졌습니다.</strong>
          <p>제출 후 크리에이트립 담당자가 내용을 확인하고 계약서 초안을 이메일로 전달합니다.</p>
        </article>
      )}
      <button className="button primary submit-button" type="button" onClick={onSubmit} disabled={submissionDisabled}>
        {draft.submittedAt ? "제출 완료" : submissionDisabled ? "업로드 중" : "입점 정보 제출하기"}
      </button>
    </section>
  );
}
